import { Fragment, useState, useEffect, useCallback, useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { useTheme, getColors } from "../ThemeContext";
import { useAIAdvice } from "../useAIAdvice";
import { useWell } from "../WellContext";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, ComposedChart,
  Cell, PieChart, Pie,
} from "recharts";

import { API } from "../config/api";
import PlotSummary from "../components/PlotSummary";

const C = {
  bg:"#050b14", panel:"#080f1e", card:"#0b1525",
  border:"#0f2040", border2:"#162a4a",
  cyan:"#00d4ff", teal:"#00ffcc", amber:"#ffb84d",
  pink:"#ff4da6", purple:"#a855f7", blue:"#3b9eff",
  red:"#ff3d5a", green:"#00ff88", orange:"#ff7c2a",
  lime:"#b0ff4a", text:"#e2eaf8", muted:"#4a6080", muted2:"#2a3f60",
};
function applyTheme(colors) { Object.assign(C, colors); }
const MONO = "'Space Mono', monospace";
const SANS = "'Syne', sans-serif";

/* Standard 2-decimal display for all setpoint / prediction / diagnostic values */
const fmt2 = (v) => {
  if (v == null || v === "") return "-";
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : String(v);
};
/* Same, but for <input type="number"> (empty string instead of a dash) */
const inp2 = (v) => {
  if (v == null || v === "") return "";
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : "";
};

/* FIELD METADATA */
const FIELD_META = {
  CsgAtOpen:              { unit:"psi",     label:"Casing Pressure @ Open"     },
  TbgAtOpen:              { unit:"psi",     label:"Tubing Pressure @ Open"     },
  LineAtOpen:             { unit:"psi",     label:"Line Pressure @ Open"       },
  CsgAtClose:             { unit:"psi",     label:"Casing Pressure @ Close"    },
  TbgAtClose:             { unit:"psi",     label:"Tubing Pressure @ Close"    },
  LineAtClose:            { unit:"psi",     label:"Line Pressure @ Close"      },
  CsgCycleMin:            { unit:"psi",     label:"Min Casing Pressure"        },
  ArrivalVelocity:        { unit:"ft/min",  label:"Plunger Arrival Velocity"   },
  ArrivalMinute:          { unit:"min",     label:"Plunger Arrival Time"       },
  VentMin:                { unit:"min",     label:"Vent Duration"              },
  TimeAfterFlow:          { unit:"min",     label:"After-Flow Duration"        },
  TimeOffTotal:           { unit:"min",     label:"Total Shut-In Time"         },
  FlowRateAtClose:        { unit:"MCF/d",   label:"Flow Rate @ Close"          },
  VolCycleTotal:          { unit:"MCF",     label:"Total Cycle Volume"         },
  ShutInEfficiency:       { unit:"×",       label:"Shut-In Efficiency"         },
  BuildupRate_psi_per_min:{ unit:"psi/min", label:"Pressure Buildup Rate"      },
  LiquidHeight_ft:        { unit:"ft",      label:"Liquid Column Height"       },
  velocity:               { unit:"ft/min",  label:"Plunger Arrival Velocity"   },
  actual:                 { unit:"",        label:"Actual Value"               },
  lstm:                   { unit:"",        label:"Deep Sequence Neural Network Predicted" },
  target:                 { unit:"",        label:"Target"                     },
};
const GENERIC_KEYS = new Set([
  "actual", "lstm", "target",
  "c1_pos", "c1_neg", "c2_pos", "c2_neg",
  "accept", "abort", "reason",
  "active", "inactive",
  "TOUR+HOLD", "RESCUE", "PR_OPT",
]);

/* PORTAL TOOLTIP */
function TTip({ active, payload }) {
  const { advice, loading, fetchAdvice, clearAdvice } = useAIAdvice();
  const prevKeyRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    function onMove(e) { setMousePos({ x: e.clientX, y: e.clientY }); }
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  const raw      = active && payload?.length ? payload[0]?.payload || {} : {};
  const cycleNum = raw.CycleSerialNumber ?? raw.cycle ?? raw.index ?? null;
  const timeStr  = raw.time || raw.timestamp || raw.date || null;
  const fieldKey = raw._actualFieldKey || null;

  // Primary field detection
  let primaryField = null;
  let primaryValue = null;
  let primaryUnit  = "";
  const allFields = [];

  if (active && payload?.length) {
    const binaryKeys = ["accept","abort","reason","active","inactive","TOUR+HOLD","RESCUE","PR_OPT"];
    const hoveredBinaryKey = payload.find(p => binaryKeys.includes(p.dataKey || p.name || ""));
    const isPrimaryBinary = !!hoveredBinaryKey;
    for (const p of payload) {
      if (p.value == null) continue;
      const rawKey    = p.dataKey || p.name || "";
      const isGeneric = GENERIC_KEYS.has(rawKey);
      const lookupKey = isGeneric && fieldKey ? fieldKey : rawKey;
      const meta      = FIELD_META[lookupKey] || FIELD_META[rawKey];
      const val       = typeof p.value === "number" ? p.value : null;
      if (val === null) continue;
      if (rawKey === "ArrivalVelocity" || rawKey === "velocity") continue; // added below

      let statusText = "";
      if (rawKey === "accept") {
        statusText = val === 1
          ? "GOOD (Cycle accepted: velocity within valid learning window range 200-1500 ft/min)"
          : "NORMAL (Cycle not accepted into learning window: velocity outside 200-1500 ft/min)";
      } else if (rawKey === "abort") {
        statusText = val === -1
          ? "CRITICAL (Cycle aborted: velocity outside safe bounds 100-3000 ft/min)"
          : "GOOD (No abort: cycle within safe velocity bounds)";
      } else if (rawKey === "reason") {
        statusText = val < 0
          ? "CRITICAL (Abort triggered: velocity outside 100-3000 ft/min bounds)"
          : "GOOD (No abort reason: velocity within bounds)";
      } else if (rawKey === "active") {
        statusText = val === 1
          ? "GOOD (Controller active: commands being sent to field equipment)"
          : "NORMAL (Controller not active this cycle)";
      } else if (rawKey === "inactive") {
        statusText = val === 1
          ? "CRITICAL (Controller inactive: well operating uncontrolled)"
          : "GOOD (Controller was active this cycle)";
      } else if (rawKey === "TOUR+HOLD") {
        statusText = val === 1
          ? "GOOD (TOUR+HOLD mode: controller learning from cycle data)"
          : "NORMAL (Not in TOUR+HOLD this cycle)";
      } else if (rawKey === "RESCUE") {
        statusText = val === 1
          ? "WARNING (RESCUE mode active: controller is correcting velocity or load factor back toward safe limits)"
          : "GOOD (No rescue needed: well operating within safe parameters)";
      } else if (rawKey === "PR_OPT") {
        statusText = val === 1
          ? "GOOD (PR_OPT mode: controller optimizing production rate)"
          : "NORMAL (Not in PR_OPT this cycle)";
      }
      if (!statusText && rawKey === "actual" && raw._actualStatus) {
        statusText = raw._actualStatus;
      }
      allFields.push({
        field:  lookupKey,
        label:  meta?.label || rawKey,
        value:  Math.round(val * 100) / 100,
        unit:   meta?.unit || "",
        status: statusText || undefined,
      });

      if (primaryField === null && meta) {
        primaryField = lookupKey;
        primaryUnit  = meta.unit || "";
        primaryValue = isGeneric && fieldKey && fieldKey !== rawKey && typeof raw[lookupKey] === "number"
          ? raw[lookupKey]
          : p.value;
      }
    }
    if (primaryField === null && payload.length > 0) {
      const p = payload[0];
      const rawKey = p.dataKey || p.name || "";
      primaryField = rawKey;
      primaryValue = p.value;
      primaryUnit = "";
    }
    const rawVel = raw.ArrivalVelocity ?? raw.velocity ?? null;
    if (rawVel != null && typeof rawVel === "number") {
      const velZone = rawVel < 350 ? "WARNING (Too Slow: Plunger May Drop Back)"
                    : rawVel > 1200 ? "CRITICAL (Too Fast: Excess Energy)"
                    : "GOOD (Optimal Velocity Range 350-1200 ft/min)";
      allFields.push({
        field: "ArrivalVelocity",
        label: "Plunger Arrival Velocity",
        value: Math.round(rawVel * 100) / 100,
        unit: "ft/min",
        status: isPrimaryBinary ? undefined : velZone,
      });
    }
    if (primaryField === null && payload.length > 0) {
      const p = payload[0];
      const rawKey = p.dataKey || p.name || "";
      primaryField = rawKey;
      primaryValue = p.value;
      primaryUnit = "";
    }
  }
  const context = {};
  Object.entries(raw).forEach(([k, v]) => {
    if (typeof v === "number" && !k.startsWith("_") && k !== "index") {
      context[k] = Math.round(v * 100) / 100;
    }
  });
  if (cycleNum != null) context.cycle_num = cycleNum;

  useEffect(() => {
    if (!active || !primaryField || primaryValue == null) return;
    const rounded = typeof primaryValue === "number"
      ? Math.round(primaryValue * 10) / 10 : primaryValue;
    const allValsKey = allFields.map(f => `${f.field}:${f.value}`).join("|");
    const key = `${primaryField}_${rounded}_${allValsKey}`;
    if (prevKeyRef.current === key) return;
    prevKeyRef.current = key;
    fetchAdvice(primaryField, rounded, primaryUnit, cycleNum, context, allFields);
    
    return clearAdvice;
  }, [active, primaryField, primaryValue, allFields, context, fetchAdvice, clearAdvice]);
  useEffect(() => {
    if (!active) {
      clearAdvice();
    }
  }, [active, clearAdvice]);

  if (!active || !payload?.length) return null;

  const TIP_W = 340;
  const TIP_H = 300;
  const GAP   = 14;
  const vw    = window.innerWidth;
  const vh    = window.innerHeight;
  let left    = mousePos.x + GAP;
  let top     = mousePos.y + GAP;
  if (left + TIP_W > vw - 8) left = mousePos.x - TIP_W - GAP;
  if (top  + TIP_H > vh - 8) top  = mousePos.y - TIP_H - GAP;

  const tooltipContent = (
    <div style={{
      position: "fixed", left, top,
      background: C.card, border: `1px solid ${C.border2}`,
      borderRadius: 12, padding: "12px 16px", fontFamily: MONO, fontSize: 11,
      boxShadow: "0 12px 40px #000e", maxWidth: TIP_W,
      zIndex: 99999, pointerEvents: "none",
    }}>
      {/* Header */}
      <div style={{ borderBottom: `1px solid ${C.border}`, paddingBottom: 7, marginBottom: 8 }}>
        {cycleNum != null && (
          <div style={{ color: C.cyan, fontWeight: 700, fontSize: 12, marginBottom: 2 }}>
            Cycle {cycleNum}
          </div>
        )}
        {timeStr && (
          <div style={{ color: C.muted, fontSize: 9 }}>{timeStr}</div>
        )}
      </div>

      {/* All series rows */}
      {payload
        .filter((p) => {
          const rawKey = p.dataKey || p.name || "";
          const chartType = raw._chartType || "";
          if (chartType === "controller_modes" || chartType === "controller_active_status") return true;
          const binaryKeys = ["accept","abort","reason","active","inactive","TOUR+HOLD","RESCUE","PR_OPT"];
          if (binaryKeys.includes(rawKey)) {
            if (rawKey === "accept"    && p.value === 0)  return false;
            if (rawKey === "abort"     && p.value === 0)  return false;
            if (rawKey === "reason"    && p.value === 0)  return false;
            if (rawKey === "active"    && p.value === 0)  return false;
            if (rawKey === "inactive"  && p.value === 0)  return false;
            if (rawKey === "TOUR+HOLD" && p.value === 0)  return false;
            if (rawKey === "RESCUE"    && p.value === 0)  return false;
            if (rawKey === "PR_OPT"    && p.value === 0)  return false;
          }
          return true;
        })
        .map((p, i, arr) => {
          const rawKey    = p.dataKey || p.name || "";
          const isGeneric = GENERIC_KEYS.has(rawKey);
          const lookupKey = isGeneric && fieldKey ? fieldKey : rawKey;
          const meta      = FIELD_META[lookupKey] || FIELD_META[rawKey];
          const chartType = raw._chartType || "";
          const isDiagnostics = chartType === "controller_modes" || chartType === "controller_active_status";
          const rawVel = raw.ArrivalVelocity ?? raw.velocity ?? null;
          const binaryKeys = ["accept","abort","reason","active","inactive","TOUR+HOLD","RESCUE","PR_OPT"];
          const isBinary = binaryKeys.includes(rawKey);

          let displayName;
          if      (rawKey === "actual")     displayName = meta?.label ? `Actual: ${meta.label}` : "Actual";
          else if (rawKey === "lstm")       displayName = "AI model";
          else if (rawKey === "target")     displayName = "Target";
          else if (rawKey === "c1_pos")     displayName = "Shut-In → +Gradient";
          else if (rawKey === "c1_neg")     displayName = "Shut-In → −Gradient";
          else if (rawKey === "c2_pos")     displayName = "After-Flow → +Gradient";
          else if (rawKey === "c2_neg")     displayName = "After-Flow → −Gradient";
          else if (rawKey === "accept")     displayName = "Learning: Accept";
          else if (rawKey === "abort")      displayName = "Learning: Abort";
          else if (rawKey === "reason")     displayName = "Abort Code";
          else if (rawKey === "active")     displayName = "Controller Active";
          else if (rawKey === "inactive")   displayName = "Controller Inactive";
          else if (rawKey === "TOUR+HOLD")  displayName = "Mode: TOUR+HOLD";
          else if (rawKey === "RESCUE")     displayName = "Mode: RESCUE";
          else if (rawKey === "PR_OPT")     displayName = "Mode: PR_OPT";
          else                              displayName = meta?.label || p.name || rawKey;

          const unit = meta?.unit || "";
          let displayValue;
          let displayColor = p.color || C.cyan;

          if (isDiagnostics && isBinary) {
            displayValue = `${(typeof p.value === "number" ? p.value : 0).toFixed(2)} ft/min`;
            if      (rawKey === "TOUR+HOLD") displayColor = p.value === 1 ? C.teal  : C.muted;
            else if (rawKey === "RESCUE")    displayColor = p.value === 1 ? C.orange : C.muted;
            else if (rawKey === "PR_OPT")    displayColor = p.value === 1 ? C.amber : C.muted;
            else if (rawKey === "active")    displayColor = p.value === 1 ? C.teal  : C.muted;
            else if (rawKey === "inactive")  displayColor = p.value === 1 ? C.red   : C.muted;
            else                             displayColor = C.muted;
          } else if (rawKey === "accept") {
            displayValue = p.value === 1 ? "Accept" : "-";
            displayColor = C.teal;
          } else if (rawKey === "abort") {
            displayValue = p.value === -1 ? "Abort" : "-";
            displayColor = C.red;
          } else if (rawKey === "reason") {
            displayValue = `Code ${p.value}`;
            displayColor = C.red;
          } else if (rawKey === "active") {
            displayValue = "Active";
            displayColor = C.teal;
          } else if (rawKey === "inactive") {
            displayValue = "Inactive";
            displayColor = C.red;
          } else if (rawKey === "TOUR+HOLD") {
            displayValue = "TOUR+HOLD";
            displayColor = C.teal;
          } else if (rawKey === "RESCUE") {
            displayValue = "RESCUE";
            displayColor = C.red;
          } else if (rawKey === "PR_OPT") {
            displayValue = "PR_OPT";
            displayColor = C.amber;
          } else {
            const formatted = typeof p.value === "number"
              ? (Math.abs(p.value) >= 100 ? p.value.toFixed(1) : p.value.toFixed(2))
              : String(p.value ?? "-");
            displayValue = unit ? `${formatted} ${unit}` : formatted;
            if (rawKey === "ArrivalVelocity" || rawKey === "velocity") {
              displayColor = typeof p.value === "number"
                ? (p.value < 350 ? C.pink : p.value > 1200 ? C.amber : C.teal)
                : (p.color || C.cyan);
            }
          }

          return (
            <div key={rawKey} style={{
              marginBottom: 4, paddingBottom: 4,
              borderBottom: i < arr.length - 1 ? `1px solid ${C.border}` : "none",
            }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:12 }}>
                <div style={{ color: C.muted, fontSize: 10, flex: 1, lineHeight: 1.3 }}>{displayName}</div>
                <span style={{ color: displayColor, fontSize: 11 }}>{displayValue}</span>
              </div>
              {isDiagnostics && isBinary && rawVel != null && (
                <div style={{ marginTop: 2, color: C.muted, fontSize: 9 }}>
                  Plunger Arrival Velocity:{" "}
                  <span style={{ color: C.cyan }}>{rawVel.toFixed(1)} ft/min</span>
                </div>
              )}
            </div>
          );
        })}
      {/* AI Recommendation box */}
      <div style={{
        marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.border}`,
        fontSize: 10, lineHeight: 1.6,
        background: loading ? `${C.muted}08` : `${C.cyan}08`,
        borderRadius: 6, padding: "6px 8px",
      }}>
        {loading
          ? <span style={{ color: C.muted }}>Analysing cycle data…</span>
          : advice
            ? (() => {
                const isPrOpt  = payload.some(p => (p.dataKey || p.name || "") === "PR_OPT"  && p.value === 1);
                const isRescue = payload.some(p => (p.dataKey || p.name || "") === "RESCUE"   && p.value === 1);
                const isAbort  = payload.some(p => (p.dataKey || p.name || "") === "abort"    && p.value === -1);
                const isInactive = payload.some(p => (p.dataKey || p.name || "") === "inactive" && p.value === 1);
                const goodStates = payload.some(p => {
                  const k = p.dataKey || p.name || "";
                  return (
                    (k === "accept"    && p.value === 1) ||
                    (k === "active"    && p.value === 1) ||
                    (k === "TOUR+HOLD" && p.value === 1) ||
                    (k === "abort"     && p.value === 0) ||
                    (k === "inactive"  && p.value === 0) ||
                    (k === "RESCUE"    && p.value === 0)
                  );
                });

                let displayAdvice = advice;
                if (isAbort || isInactive) {
                  displayAdvice = displayAdvice.replace(/^[◆▲▽▶]\s/, "■ ");
                }
                else if (isRescue) {
                  displayAdvice = displayAdvice.replace(/^[◆■▽▶]\s/, "▲ ");
                }
                else if (isPrOpt) {
                  // PR_OPT bars are amber, so the hover must read WARNING (never critical / good)
                  displayAdvice = displayAdvice.replace(/^[◆■▽▶]\s/, "▲ ");
                  displayAdvice = displayAdvice.replace(/\bcritical(ly)?\b/gi, m => (m[0] === "C" ? "Warning" : "warning"));
                }
                else if (goodStates) {
                  displayAdvice = displayAdvice.replace(/^[■▲▽▶]\s/, "◆ ");
                }
                else {
                  const actualSt = raw._actualStatus || "";
                  if (actualSt.startsWith("CRITICAL")) {
                    displayAdvice = displayAdvice.replace(/^[◆▲▽▶]\s/, "■ ");
                  } else if (actualSt.startsWith("WARNING")) {
                    displayAdvice = displayAdvice.replace(/^[◆▽▶]\s/, "▲ ");
                  } else if (actualSt.startsWith("GOOD")) {
                    displayAdvice = displayAdvice.replace(/^[■▲▽▶]\s/, "◆ ");
                  }
                }

                return renderAdvice(displayAdvice);
              })()
            : <span style={{ color: C.muted }}>Hover a data point for AI advice</span>
        }
      </div>
    </div>
  );

  return ReactDOM.createPortal(tooltipContent, document.body);
}

const TTIP_PROPS = {
  content: <TTip />,
  wrapperStyle: {
    visibility: "hidden", pointerEvents: "none",
    position: "fixed", top: 0, left: 0,
    width: 0, height: 0, overflow: "visible", zIndex: 0,
  },
  allowEscapeViewBox: { x: true, y: true },
  cursor: { fill: "rgba(255,255,255,0.05)" },
};

/* CYCLE TICK  */
function cycleTickFmt(v) {
  if (v == null || v === "") return "";
  const n = Number(v);
  if (!isNaN(n)) return `C${n}`;
  if (String(v).includes(":") || String(v).includes("-")) return String(v).slice(0, 8);
  return String(v);
}

const CYCLE_XAXIS_LABEL = {
  value: "Cycle", position: "insideBottomRight", offset: 0,
  fill: C.muted, fontSize: 9, fontFamily: MONO,
};

/* ───────────── TIME-WINDOW HELPERS (timestamps on every plot) ───────────── */
const MON = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const pad2 = n => String(n).padStart(2, "0");
const HOUR = 3600000, DAY = 86400000;

// backend sends "YYYY-MM-DD HH:MM" :  parsed as plain clock time (no timezone shifting)
function parseT(s) {
  if (!s) return null;
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]).getTime() : null;
}
const fmtDT   = ms => { const d = new Date(ms); return `${d.getDate()} ${MON[d.getMonth()]} ${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };
const fmtD    = ms => { const d = new Date(ms); return `${d.getDate()} ${MON[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`; };
const fmtHM   = ms => { const d = new Date(ms); return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };
const toInput = ms => { const d = new Date(ms); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; };

function fmtSpan(ms) {
  if (ms < HOUR)    return `${Math.max(1, Math.round(ms / 60000))} min`;
  if (ms < 2 * DAY) return `${Math.round(ms / HOUR)} h`;
  const d = Math.round(ms / DAY);
  return d >= 14 ? `${d} days (${(d / 7).toFixed(1)} wk)` : `${d} days`;
}
function fmtAgo(ms) {
  if (ms < HOUR)    return "just now";
  if (ms < 2 * DAY) return `${Math.round(ms / HOUR)} h ago`;
  return `${Math.round(ms / DAY)} d ago`;
}

const WIN_PRESETS = [
  { id: "24h",   label: "24 H",   ms: DAY },
  { id: "7d",    label: "7 D",    ms: 7 * DAY },
  { id: "30d",   label: "30 D",   ms: 30 * DAY },
  { id: "90d",   label: "90 D",   ms: 90 * DAY },
  { id: "cyc30", label: "LAST 30 CYC", cycles: 30 },
  { id: "all",   label: "ALL" },
];
const MIN_POINTS = 3;   // fewer cycles than this cannot draw a meaningful trend

// presets are measured back from the NEWEST cycle in the data, not from today's date
function applyWindow(rows, w) {
  if (!rows.length || w.mode === "all") return rows;
  if (w.mode === "cyc30") return rows.slice(-30);
  if (w.mode === "custom") {
    const f = w.from ? parseT(`${w.from} 00:00`) : -Infinity;
    const t = w.to   ? parseT(`${w.to} 23:59`)   :  Infinity;
    return rows.filter(d => d._t != null && d._t >= f && d._t <= t);
  }
  const ts = rows.map(d => d._t).filter(t => t != null);
  if (!ts.length) return rows;
  const tMax = Math.max(...ts);
  const pr = WIN_PRESETS.find(x => x.id === w.mode);
  return rows.filter(d => d._t != null && d._t >= tMax - pr.ms);
}

/* Second row of the X axis = timestamp of the cycle sitting at that position. */
function DateTick({ x, y, payload, index, lookup, lastIdx, shortSpan, compact }) {
  const t = lookup.get(payload.value);
  if (t == null) return null;
  const anchor = index === 0 ? "start" : index === lastIdx ? "end" : "middle";
  if (compact) {
    return (
      <g transform={`translate(${x},${y})`}>
        <text textAnchor={anchor} fill={C.amber} fontSize={8} fontFamily={MONO} dy={11}>
          {shortSpan ? `${fmtHM(t)} ${fmtD(t)}` : `${fmtD(t)} ${fmtHM(t)}`}
        </text>
      </g>
    );
  }
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor={anchor} fill={C.amber} fontSize={9} fontFamily={MONO} dy={11}>{shortSpan ? fmtHM(t) : fmtD(t)}</text>
      <text textAnchor={anchor} fill={C.muted} fontSize={8} fontFamily={MONO} dy={22}>{shortSpan ? fmtD(t) : fmtHM(t)}</text>
    </g>
  );
}

/* Call as a function (NOT <Component/>): recharts only recognises <XAxis> as a direct child.
   Rows must carry `_t` (ms) and `index`. compact = single-line labels for the small charts. */
function dateXAxis(rows, nTicks = 5, compact = false) {
  const stamped = rows.filter(r => r._t != null);
  const last = stamped.length - 1;
  const idx = new Set();
  for (let i = 0; i < nTicks && last >= 0; i++) idx.add(Math.round((i * last) / Math.max(nTicks - 1, 1)));
  const ticks = [...idx].map(i => stamped[i].index);
  const lookup = new Map(stamped.map(r => [r.index, r._t]));
  const span = stamped.length > 1 ? (stamped[last]._t - stamped[0]._t) : 0;
  return (
    <XAxis xAxisId="date" dataKey="index" ticks={ticks} interval={0} tickLine={false} axisLine={false} height={compact ? 24 : 32}
      tick={<DateTick lookup={lookup} lastIdx={ticks.length - 1} shortSpan={span > 0 && span < 3 * DAY} compact={compact}/>}/>
  );
}

// "31 Jul '20 06:50 → 11 Jan '21 13:31 · 80 cyc" for the chip in each chart header
function rangeTag(rows, extra = "") {
  const st = rows.filter(r => r._t != null);
  if (!st.length) return `${rows.length} cyc${extra}`;
  return `${fmtD(st[0]._t)} ${fmtHM(st[0]._t)} → ${fmtD(st[st.length - 1]._t)} ${fmtHM(st[st.length - 1]._t)} · ${rows.length} cyc${extra}`;
}

/* DATA WINDOW BAR: one selector for every plot on this page */
function TimeWindowBar({ win, setWin, base, view }) {
  const ts   = base.map(d => d._t).filter(t => t != null);
  const tMin = ts.length ? Math.min(...ts) : null;
  const tMax = ts.length ? Math.max(...ts) : null;
  const vs   = view.filter(d => d._t != null);
  const vStart = vs.length ? vs[0]._t : null, vEnd = vs.length ? vs[vs.length - 1]._t : null;
  const hasTime = vStart != null && vEnd != null;
  const isAll = win.mode === "all";
  const ago = tMax != null ? Date.now() - tMax : null;
  const agoC = ago != null && ago > DAY ? C.orange : C.green;
  const counts = Object.fromEntries(WIN_PRESETS.map(pr => [pr.id, applyWindow(base, { mode: pr.id }).length]));
  const btn = (active, disabled) => ({
    fontFamily: MONO, fontSize: 10, letterSpacing: 1, padding: "5px 11px", borderRadius: 6, cursor: disabled ? "not-allowed" : "pointer",
    background: active ? `${C.amber}22` : "transparent", color: disabled ? C.muted2 : active ? C.amber : C.muted,
    border: `1px solid ${active ? C.amber : C.border2}`, opacity: disabled ? 0.55 : 1, whiteSpace: "nowrap",
  });
  const dateInput = { fontFamily: MONO, fontSize: 10, background: C.panel, color: C.text, border: `1px solid ${C.border2}`, borderRadius: 6, padding: "4px 8px" };

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.amber}`, borderRadius: 12, padding: "14px 18px", marginBottom: 18 }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: 2, color: C.muted }}>DATA WINDOW</span>
        <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: C.text }}>
          {hasTime ? `${fmtDT(vStart)}  →  ${fmtDT(vEnd)}` : "no timestamps available"}
        </span>
        {hasTime && <span style={{ fontFamily: MONO, fontSize: 10, color: C.amber, background: `${C.amber}14`, border: `1px solid ${C.amber}33`, borderRadius: 5, padding: "2px 8px" }}>
          {isAll ? "CUMULATIVE" : "WINDOW"} · {fmtSpan(Math.max(vEnd - vStart, 0))}
        </span>}
        <span style={{ fontFamily: MONO, fontSize: 10, color: C.cyan, background: `${C.cyan}12`, border: `1px solid ${C.cyan}33`, borderRadius: 5, padding: "2px 8px" }}>
          {view.length} of {base.length} cycles
        </span>
        {ago != null && (
          <span title="Presets are measured back from the newest cycle in the data, not from today's date"
            style={{ fontFamily: MONO, fontSize: 10, color: agoC, background: `${agoC}12`, border: `1px solid ${agoC}33`, borderRadius: 5, padding: "2px 8px" }}>
            Latest cycle {fmtDT(tMax)} · {fmtAgo(ago)}
          </span>
        )}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
        {WIN_PRESETS.map(pr => {
          const n = counts[pr.id];
          const disabled = pr.id !== "all" && n < MIN_POINTS;
          return (
            <button key={pr.id} disabled={disabled} onClick={() => setWin({ mode: pr.id, from: "", to: "" })}
              title={disabled ? `Only ${n} cycle${n === 1 ? "" : "s"} in this window: too few to plot a trend. Try a wider window.` : `${n} cycles`}
              style={btn(win.mode === pr.id, disabled)}>
              {pr.label} <span style={{ opacity: 0.7 }}>· {n}</span>
            </button>
          );
        })}
        <button onClick={() => setWin(w => ({ mode: "custom", from: w.from || (tMin != null ? toInput(tMin) : ""), to: w.to || (tMax != null ? toInput(tMax) : "") }))}
          style={btn(win.mode === "custom", false)}>CUSTOM RANGE</button>
        {win.mode === "custom" && (
          <>
            <input type="date" value={win.from} min={tMin != null ? toInput(tMin) : undefined} max={win.to || (tMax != null ? toInput(tMax) : undefined)}
              onChange={e => setWin(w => ({ ...w, from: e.target.value }))} style={dateInput}/>
            <span style={{ fontFamily: MONO, fontSize: 10, color: C.muted }}>to</span>
            <input type="date" value={win.to} min={win.from || (tMin != null ? toInput(tMin) : undefined)} max={tMax != null ? toInput(tMax) : undefined}
              onChange={e => setWin(w => ({ ...w, to: e.target.value }))} style={dateInput}/>
          </>
        )}
      </div>
      {view.length < MIN_POINTS && (
        <div style={{ fontFamily: MONO, fontSize: 10, color: C.orange, marginTop: 8 }}>
          ⚠ Only {view.length} cycle{view.length === 1 ? "" : "s"} in this range: widen the window to see a trend.
        </div>
      )}
    </div>
  );
}
function PulseDot({ color=C.green }) {
  return (
    <span style={{ position:"relative", display:"inline-flex", width:8, height:8, flexShrink:0 }}>
      <span style={{ position:"absolute", inset:0, borderRadius:"50%", background:color, animation:"pulse-ring 1.8s ease-out infinite", opacity:0 }}/>
      <span style={{ position:"absolute", inset:1, borderRadius:"50%", background:color, boxShadow:`0 0 6px ${color}` }}/>
    </span>
  );
}

function findPlotCardTitle(nodes) {
  const list = Array.isArray(nodes) ? nodes : [nodes];
  for (const node of list) {
    if (!node || typeof node !== "object" || !node.props) continue;
    const typeName = (node.type && (node.type.displayName || node.type.name)) || "";
    if (typeName === "SectionTitle") {
      const value = node.props.children;
      if (typeof value === "string" && value.trim()) return value.trim();
      if (Array.isArray(value)) {
        const text = value.filter(v => typeof v === "string").join(" ").trim();
        if (text) return text;
      }
    }
    const nested = findPlotCardTitle(node.props.children);
    if (nested) return nested;
  }
  return "AI Controller plot";
}

function Card({ children, accent=C.cyan, style={} }) {
  const plotTitle = findPlotCardTitle(children);
  return (
    <div style={{
      background:C.card, border:`1px solid ${C.border}`, borderRadius:14,
      padding:"20px 18px", position:"relative", overflow:"hidden", ...style
    }}>
      <div style={{ position:"absolute", top:0, left:20, right:20, height:2, background:`linear-gradient(to right, transparent, ${accent}88, transparent)` }}/>
      {children}
      <PlotSummary inline title={plotTitle} subtitle={`AI Controller plot for ${plotTitle}; interpret the displayed chart using its available data`} accent={accent}>{children}</PlotSummary>
    </div>
  );
}

function SectionTitle({ children, sub, tag }) {
  return (
    <div style={{ marginBottom:14, ...(tag ? { display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:10, flexWrap:"wrap", flex:"1 1 auto" } : {}) }}>
      <div>
        <div style={{ fontFamily:SANS, fontWeight:700, fontSize:13, color:C.text }}>{children}</div>
        {sub && <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, marginTop:2 }}>{sub}</div>}
      </div>
      {tag && (
        <div style={{ fontFamily:MONO, fontSize:9, color:C.amber, background:`${C.amber}12`, border:`1px solid ${C.amber}33`, borderRadius:5, padding:"3px 8px", whiteSpace:"nowrap" }}>
          ⏱ {tag}
        </div>
      )}
    </div>
  );
}

function TabBtns({ options, value, onChange, accent=C.cyan }) {
  return (
    <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
      {options.map(o => (
        <button key={o.id} onClick={() => onChange(o.id)} style={{
          fontFamily:MONO, fontSize:10, padding:"5px 14px", borderRadius:6,
          border:`1px solid ${value===o.id ? accent+"66" : C.border}`,
          background:value===o.id ? `${accent}18` : "transparent",
          color:value===o.id ? accent : C.muted, cursor:"pointer"
        }}>
          {o.label}
          {o.badge != null && (
            <span style={{ marginLeft:6, background:`${accent}33`, color:accent, padding:"1px 6px", borderRadius:10, fontSize:9 }}>{o.badge}</span>
          )}
        </button>
      ))}
    </div>
  );
}

/* useFetch hook */
function useFetch(url, refreshKey = 0) {
  const { apiUrl, selectedWell } = useWell();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    const path = url.replace(API, "").replace("http://localhost:5000/api", "");
    const fullUrl = apiUrl(path);
    setLoading(true); setError(null);
    axios.get(fullUrl)
      .then(r  => { if (!cancelled) { setData(r.data);    setLoading(false); } })
      .catch(e => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [url, refreshKey, selectedWell]);
  return { data, loading, error };
}

/* FILTER BAR */
function FilterBar({ filters, onChange }) {
  const { wells, selectedWell, setSelectedWell } = useWell();
  const [now, setNow] = useState(new Date().toLocaleString());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date().toLocaleString()), 1000);
    return () => clearInterval(t);
  }, []);
  const shortName = w => w.split("/").pop();

  return (
    <Card accent={C.teal} style={{ marginBottom:18 }}>
      <div style={{ display:"flex", alignItems:"flex-end", gap:20, flexWrap:"wrap" }}>
        <div>
          <div style={{ fontFamily:MONO, fontSize:9, color:C.muted, marginBottom:6, textTransform:"uppercase", letterSpacing:1 }}>Well Name</div>
          <select value={selectedWell} onChange={e => setSelectedWell(e.target.value)} style={{
            background:C.panel, border:`1px solid ${C.border2}`, borderRadius:8,
            color:C.text, fontFamily:MONO, fontSize:11, padding:"9px 14px",
            outline:"none", cursor:"pointer", minWidth:160
          }}>
            {wells.map(w => <option key={w} value={w}>{shortName(w)}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontFamily:MONO, fontSize:9, color:C.muted, marginBottom:6, textTransform:"uppercase", letterSpacing:1 }}>Max Cycles</div>
          <select value={filters.limit} onChange={e => onChange({ ...filters, limit: e.target.value })} style={{
            background:C.panel, border:`1px solid ${C.border2}`, borderRadius:8,
            color:C.text, fontFamily:MONO, fontSize:11, padding:"9px 14px",
            outline:"none", cursor:"pointer"
          }}>
            {[50,100,150,200].map(v => <option key={v}>{v}</option>)}
          </select>
        </div>
        <div style={{ marginLeft:"auto", textAlign:"right" }}>
          <div style={{ display:"flex", alignItems:"center", gap:7, justifyContent:"flex-end" }}>
            <PulseDot color={C.green}/>
            <span style={{ fontFamily:MONO, fontSize:10, color:C.green }}>CST LIVE</span>
          </div>
          <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, marginTop:3 }}>{now}</div>
        </div>
      </div>
    </Card>
  );
}
/* CONTROLLER HEADER*/
function ControllerHeader({ mode, onModeChange, onParams, targetValues, interlocked, aiEnabled, onToggleAI }) {
  const [writing, setWriting] = useState(true);
  const effectiveWriting = interlocked ? false : writing;

  const MODES = [
    { id:"pr", label:"Production Rate", value: targetValues.pr ? `${targetValues.pr.toFixed(0)} MCF/day` : "450 MCF/day", color:C.teal  },
    { id:"cp", label:"Casing Pressure", value: targetValues.cp ? `${targetValues.cp.toFixed(0)} psi`     : "-",           color:C.cyan  },
    { id:"tp", label:"Tubing Pressure", value: targetValues.tp ? `${targetValues.tp.toFixed(0)} psi`     : "-",           color:C.amber },
  ];

  return (
    <Card accent={C.cyan} style={{ marginBottom:18 }}>
      <div style={{ display:"flex", alignItems:"center", gap:16, flexWrap:"wrap" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ fontFamily:MONO, fontSize:11, color:C.muted }}>Controller Writing</div>
          <div onClick={() => !interlocked && setWriting(v => !v)} style={{
            width:44, height:24, borderRadius:12, cursor: interlocked ? "not-allowed" : "pointer",
            background: effectiveWriting ? `linear-gradient(to right, ${C.teal}, ${C.cyan})` : C.muted2,
            position:"relative", transition:"background 0.3s", flexShrink:0, opacity: interlocked ? 0.5 : 1,
          }}>
            <div style={{
              position:"absolute", top:3, left: effectiveWriting ? 22 : 3,
              width:18, height:18, borderRadius:"50%", background:"#fff",
              transition:"left 0.3s", boxShadow:"0 1px 4px #0006"
            }}/>
          </div>
          <span style={{ fontFamily:MONO, fontSize:11, fontWeight:700, color: effectiveWriting ? C.green : C.muted }}>
            {interlocked ? "BLOCKED" : effectiveWriting ? "ON" : "OFF"}
          </span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 14px", background:`${C.teal}12`, border:`1px solid ${C.teal}44`, borderRadius:8 }}>
          <PulseDot color={C.teal}/>
          <div>
            <div style={{ fontFamily:MONO, fontSize:11, fontWeight:700, color:C.teal }}>Controller Active</div>
            <div style={{ fontFamily:MONO, fontSize:9, color:C.muted }}>Commands are being sent to the field</div>
          </div>
        </div>
        <div style={{ display:"flex", gap:8, marginLeft:"auto", flexWrap:"wrap" }}>
          {MODES.map(m => (
            <button key={m.id} onClick={() => onModeChange(m.id)} style={{
              background: mode===m.id ? `linear-gradient(135deg, ${m.color}cc, ${m.color}88)` : `${m.color}11`,
              border:`1px solid ${m.color}${mode===m.id ? "ff" : "44"}`,
              borderRadius:8, padding:"8px 16px", cursor:"pointer",
              transition:"all 0.18s", transform: mode===m.id ? "scale(1.04)" : "scale(1)",
            }}>
              <div style={{ fontFamily:MONO, fontSize:9, color: mode===m.id ? "#000" : C.muted, textTransform:"uppercase" }}>{m.label}</div>
              <div style={{ fontFamily:MONO, fontSize:12, fontWeight:700, color: mode===m.id ? "#000" : m.color }}>{m.value}</div>
            </button>
          ))}
        </div>
        <button onClick={onParams} style={{
          background:`${C.pink}18`, border:`1px solid ${C.pink}55`, borderRadius:8,
          padding:"9px 18px", fontFamily:SANS, fontWeight:700, fontSize:11,
          color:C.pink, cursor:"pointer", letterSpacing:1
        }}>[SETTINGS] Parameters</button>
        <button onClick={onToggleAI} disabled={interlocked} style={{
          background: aiEnabled ? `${C.blue}22` : C.blue,
          border:`1px solid ${C.blue}`, borderRadius:8,
          padding:"9px 18px", fontFamily:SANS, fontWeight:700, fontSize:11,
          color: aiEnabled ? C.blue : "#fff", cursor:interlocked ? "not-allowed" : "pointer",
          letterSpacing:1, opacity:interlocked ? 0.5 : 1,
        }}>{aiEnabled ? "✓ AI ENABLED" : "ENABLE AI"}</button>
      </div>
      <div style={{
        marginTop:12, padding:"8px 16px",
        background:`${MODES.find(m=>m.id===mode)?.color || C.teal}0a`,
        border:`1px solid ${MODES.find(m=>m.id===mode)?.color || C.teal}33`,
        borderRadius:8, display:"flex", alignItems:"center", gap:10
      }}>
        <span style={{ fontFamily:MONO, fontSize:10, color:C.muted }}>Active Target Mode:</span>
        <span style={{ fontFamily:MONO, fontSize:11, fontWeight:700, color:MODES.find(m=>m.id===mode)?.color || C.teal }}>
          {MODES.find(m=>m.id===mode)?.label}: {MODES.find(m=>m.id===mode)?.value}
        </span>
      </div>
      {effectiveWriting && !interlocked && (
        <div style={{
          marginTop:10, padding:"10px 16px",
          background:"linear-gradient(to right, #3a2a0a, #2a1a05)",
          border:`1px solid ${C.amber}44`, borderRadius:8,
          display:"flex", alignItems:"center", gap:10
        }}>
          <span style={{ fontSize:16 }}>▲</span>
          <div>
            <div style={{ fontFamily:MONO, fontSize:11, fontWeight:700, color:C.amber }}>Controller Write Mode Active</div>
          </div>
        </div>
      )}
      {interlocked && (
        <div style={{
          marginTop:10, padding:"10px 16px",
          background:"linear-gradient(to right, #3a0a0a, #2a0505)",
          border:`1px solid ${C.red}44`, borderRadius:8,
          display:"flex", alignItems:"center", gap:10
        }}>
          <span style={{ fontSize:16 }}>■</span>
          <div>
            <div style={{ fontFamily:MONO, fontSize:11, fontWeight:700, color:C.red }}>Controller Writing Disabled: Interlock Engaged</div>
            <div style={{ fontFamily:MONO, fontSize:10, color:C.muted }}>
              No commands are being sent to the field until the interlock is reset.
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

/* SETPOINTS */
function AppliedSetpoints({ shutinData, afterflowData, predictions, onRefresh, refreshing, interlocked, overrideInterlock, onToggleOverride }) {
  const lp = predictions?.wells?.[0]?.model_prediction || {};
  const showPred = (!interlocked || overrideInterlock) && lp._valid !== false; // missing flag = older response, still shown
  const predVel  = showPred && typeof lp.ArrivalVelocity === "number" ? lp.ArrivalVelocity : null;
  const predFlow = showPred && typeof lp.FlowRateAtClose === "number" ? lp.FlowRateAtClose : null;
  const predVol  = showPred && typeof lp.VolCycleTotal   === "number" ? lp.VolCycleTotal   : null;
  const plunger  = showPred ? lp.PlungerArrived : null;
  const tubingOver = (shutinData?.summary?.CsgAtOpen?.mean != null && shutinData?.summary?.TbgAtOpen?.mean != null)
    ? shutinData.summary.CsgAtOpen.mean - shutinData.summary.TbgAtOpen.mean
    : null;
  const afMin      = afterflowData?.FlowRateAtClose?.mean;
  const velColor   = predVel == null ? C.muted : predVel < 350 ? C.red : predVel > 1200 ? C.amber : C.green;

  const boxes = [
    { icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="9" width="20" height="6" rx="3"/><line x1="9" y1="9" x2="9" y2="15"/><line x1="15" y1="9" x2="15" y2="15"/><circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none"/></svg>,
      label:"RECOMMENDED TUBING OVER STATIC",      main: tubingOver?.toFixed(0) ?? "-", unit:"psi"     },
    { icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="5 12 12 5 19 12"/><polyline points="12 5 12 19"/><line x1="5" y1="19" x2="19" y2="19"/></svg>,
      label:"AFTERFLOW MINIMUM FLOWRATE SETPOINT",  main: afMin?.toFixed(0)       ?? "-", unit:"MCF/day" },
    { icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M6.5 13.5A5.5 5.5 0 0 1 12 7"/><path d="M17.5 13.5A5.5 5.5 0 0 0 12 7"/><line x1="12" y1="12" x2="15.5" y2="8.5"/><circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none"/></svg>,
      label:"DEEP SEQUENCE NEURAL NETWORK NEXT PREDICTED VELOCITY",          main: predVel?.toFixed(0)    ?? "-", unit:"ft/min", mainColor: velColor },
    { icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="2 18 8 12 13 17 22 6"/><circle cx="22" cy="6" r="2" fill="currentColor" stroke="none"/></svg>,
      label:"DEEP SEQUENCE NEURAL NETWORK NEXT PREDICTED FLOW RATE",         main: predFlow?.toFixed(1)   ?? "-", unit:"MCF/d",  mainColor: C.amber  },
    { icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="6" rx="8" ry="3"/><path d="M4 6v12c0 1.66 3.58 3 8 3s8-1.34 8-3V6"/><path d="M4 12c0 1.66 3.58 3 8 3s8-1.34 8-3"/></svg>,
      label:"DEEP SEQUENCE NEURAL NETWORK NEXT PREDICTED VOLUME",            main: predVol?.toFixed(2)    ?? "-", unit:"MCF",    mainColor: C.teal   },
    { icon:<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="8 12 11 15 16 9"/></svg>,
      label:"DEEP SEQUENCE NEURAL NETWORK PLUNGER ARRIVAL",                  main: plunger                ?? "-", unit:"",
      mainColor: plunger === "Yes" ? C.green : plunger === "No" ? C.red : C.muted },
  ];

  return (
    <Card accent={C.teal} style={{ marginBottom:18 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
        <SectionTitle>Current Applied Setpoints</SectionTitle>

        {/* ── FIXED REFRESH BUTTON ── */}
        <button
          onClick={onRefresh}
          disabled={refreshing}
          style={{
            fontFamily: MONO,
            fontSize: 10,
            padding: "5px 14px",
            borderRadius: 6,
            border: `1px solid ${refreshing ? C.muted2 : C.teal + "66"}`,
            background: refreshing ? `${C.muted2}22` : `${C.teal}10`,
            color: refreshing ? C.muted : C.teal,
            cursor: refreshing ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
            transition: "all 0.2s",
          }}
        >
          <span style={{
            display: "inline-block",
            animation: refreshing ? "spin-slow 0.8s linear infinite" : "none",
          }}>↻</span>
          {refreshing ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {interlocked && (
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10,
                       padding:"8px 12px", marginBottom:10, borderRadius:8,
                       background: overrideInterlock ? "rgba(255,180,0,0.08)" : "rgba(255,60,60,0.08)",
                       border:`1px solid ${overrideInterlock ? C.amber : C.red}` }}>
          <span style={{ fontFamily:MONO, fontSize:10, color: overrideInterlock ? C.amber : C.red }}>
            {overrideInterlock
              ? "⚠ Viewing predictions with interlock overridden: not verified safe, no auto-write."
              : "Interlock engaged: predictions hidden. You can still view them at your own risk."}
          </span>
          <button onClick={onToggleOverride} style={{
            fontFamily:MONO, fontSize:10, fontWeight:700, padding:"4px 10px", borderRadius:6,
            cursor:"pointer", border:`1px solid ${overrideInterlock ? C.amber : C.border}`,
            background: overrideInterlock ? C.amber : "transparent", color: overrideInterlock ? "#000" : C.muted,
          }}>
            {overrideInterlock ? "Hide again" : "Show anyway"}
          </button>
        </div>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:14 }}>
        {boxes.map(b => (
          <div key={b.label} style={{
            background: C.panel, borderRadius:12, padding:"16px 18px",
            display:"flex", alignItems:"flex-start", gap:12,
            opacity: refreshing ? 0.5 : 1,
            transition: "opacity 0.3s",
          }}>
            <span style={{ display:"flex", alignItems:"center", color:b.mainColor||C.cyan, marginTop:2, flexShrink:0 }}>{b.icon}</span>
            <div style={{ flex:1 }}>
              <div style={{ fontFamily:MONO, fontSize:9, color:C.muted, textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>{b.label}</div>
              <div style={{ display:"flex", alignItems:"baseline", gap:6, lineHeight:1 }}>
                <span style={{ fontFamily:SANS, fontWeight:700, fontSize:27, letterSpacing:"-0.02em", color: b.mainColor || C.text, lineHeight:1 }}>
                  {refreshing ? "-" : b.main}
                </span>
                {b.unit && !refreshing && (
                  <span style={{ fontFamily:MONO, fontSize:11, color:C.muted, lineHeight:1, alignSelf:"flex-end", paddingBottom:"1px" }}>{b.unit}</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

/* PREDICTION HELPERS (used by Actual vs. Target plot)
   /predict/batch can return the per-cycle predictions under different keys, and each
   item can hold the values at the top level or inside model_prediction. These helpers
   read all of those shapes so the Deep Sequence Neural Network line always finds its data. */
function extractPredList(predictions) {
  if (!predictions) return [];
  if (Array.isArray(predictions)) return predictions;
  if (Array.isArray(predictions.predictions)) return predictions.predictions;
  if (Array.isArray(predictions.results))     return predictions.results;
  if (Array.isArray(predictions.data))        return predictions.data;
  const w0 = predictions.wells?.[0];
  if (Array.isArray(w0?.predictions)) return w0.predictions;
  if (Array.isArray(w0?.results))     return w0.results;
  return [];
}

function readPredValue(item, col) {
  if (item == null || typeof item !== "object") return null;
  const sources = [item, item.model_prediction, item.prediction, item.predicted, item.pred];
  for (const src of sources) {
    if (src && typeof src === "object" && src[col] != null) {
      const n = parseFloat(src[col]);
      if (Number.isFinite(n)) return n;
    }
  }
  return null;
}

/* PRODUCTION & VELOCITY */
function ProductionAndVelocity({ filteredData, shutinData, afterflowData, predictions, predSeries, mode, onModeChange }) {
  const tsData   = filteredData || [];
  const predList = extractPredList(predictions);
  const nextPred = predictions?.wells?.[0]?.model_prediction || null;
  const { fetchAdvice: bgPrefetch } = useAIAdvice();
  
  const colMap   = { pr:"FlowRateAtClose", cp:"CsgAtOpen",       tp:"TbgAtOpen"   };
  const unitMap  = { pr:"MCF/day",         cp:"psi",             tp:"psi"         };
  const colorMap = { pr:C.teal,            cp:C.cyan,            tp:C.amber       };
  const nameMap  = { pr:"Production Rate", cp:"Casing Pressure", tp:"Tubing Over" };
  const col      = colMap[mode];
  const unit     = unitMap[mode];
  const color    = colorMap[mode];
  const targets  = { pr:afterflowData?.FlowRateAtClose?.mean, cp:shutinData?.summary?.CsgAtOpen?.mean, tp:shutinData?.summary?.TbgAtOpen?.mean };
  const target   = targets[mode];

  const slicedRows = tsData.slice(-80);
  // Align predictions with the cycles from the END (last prediction = last cycle shown)
  const predOffset = predList.length - slicedRows.length;

  // Per-cycle Deep Sequence NN predictions from /controller/predicted-series
  // (one-step-ahead: the model sees the previous cycles and predicts THIS cycle).
  const seriesRows = predSeries?.series || [];
  const serialMap  = new Map();
  seriesRows.forEach(s => {
    if (s.CycleSerialNumber != null) serialMap.set(Number(s.CycleSerialNumber), s.prediction);
  });
  const useSerial = serialMap.size > 0 &&
    slicedRows.some(r => r.CycleSerialNumber != null && serialMap.has(Number(r.CycleSerialNumber)));
  const seriesOffset = seriesRows.length - slicedRows.length;
  // Align by the cycle's own position (`index`) so the predicted line stays on the right cycles
  // even when a time window is selected (tail-alignment is only correct when the window ends at the newest cycle).
  const indexMap = new Map();
  seriesRows.forEach(s => { if (s.index != null) indexMap.set(Number(s.index), s.prediction); });
  const useIndex = !useSerial && indexMap.size > 0 &&
    slicedRows.some(r => r.index != null && indexMap.has(Number(r.index)));
  const seriesValue = (row, i) => {
    let pred = null;
    if (useSerial) {
      if (row.CycleSerialNumber != null) pred = serialMap.get(Number(row.CycleSerialNumber));
    } else if (useIndex) {
      if (row.index != null) pred = indexMap.get(Number(row.index));
    } else {
      const k = seriesOffset + i;
      if (k >= 0) pred = seriesRows[k]?.prediction;
    }
    return readPredValue(pred, col);
  };

  const baseRows = slicedRows.map((row, i) => {
    const actualVal = row[col] != null ? parseFloat(row[col]) : null;
    const pIdx      = predOffset + i;
    const lstmVal   = seriesValue(row, i) ?? (pIdx >= 0 ? readPredValue(predList[pIdx], col) : null);
    let actualStatus = undefined;
    if (actualVal != null) {
      if (actualVal === 0) {
        actualStatus = `CRITICAL (Zero ${nameMap[mode]}: no flow / failed cycle)`;
      } else if (target != null) {
        const pct = (actualVal / target) * 100;
        if (pct < 30)
          actualStatus = `CRITICAL (${actualVal.toFixed(1)} ${unit} is severely below target ${target.toFixed(1)} ${unit})`;
        else if (pct < 60)
          actualStatus = `WARNING (${actualVal.toFixed(1)} ${unit} is significantly below target ${target.toFixed(1)} ${unit})`;
        else if (pct < 85)
          actualStatus = `SLIGHTLY below target, ${actualVal.toFixed(1)} ${unit} vs target ${target.toFixed(1)} ${unit}: MARGINALLY LOW`;
        else if (pct <= 115)
          actualStatus = `GOOD (${actualVal.toFixed(1)} ${unit} is near target ${target.toFixed(1)} ${unit})`;
        else if (pct <= 140)
          actualStatus = `SLIGHTLY above target, ${actualVal.toFixed(1)} ${unit} vs target ${target.toFixed(1)} ${unit}: MARGINALLY HIGH`;
        else if (pct <= 180)
          actualStatus = `WARNING (${actualVal.toFixed(1)} ${unit} significantly exceeds target ${target.toFixed(1)} ${unit})`;
        else
          actualStatus = `CRITICAL (${actualVal.toFixed(1)} ${unit} is critically above target ${target.toFixed(1)} ${unit})`;
      }
    }
  
    return {
      // row is spread FIRST so it can never overwrite actual / lstm / target below
      ...row,
      label: row.time || String(row.index ?? i),
      index: row.index ?? i, time: row.time || null, date: row.date || null,
      CycleSerialNumber: row.CycleSerialNumber ?? row.index ?? i,
      _actualFieldKey: col,
      _actualStatus: actualStatus,   
      actual: actualVal,
      lstm:   lstmVal,
      target,
    };
  });

  // Fallback: if the batch response has no per-cycle values for this field, plot the
  // model's NEXT-cycle prediction (same value shown in "Current Applied Setpoints")
  // as one extra point after the last cycle so the line/marker is still visible.
  const combined = (() => {
    const hasAnyLstm = baseRows.some(d => d.lstm != null);
    if (hasAnyLstm || !baseRows.length || !nextPred || nextPred._valid === false) return baseRows;
    const v = parseFloat(nextPred[col]);
    if (!Number.isFinite(v)) return baseRows;
    const last    = baseRows[baseRows.length - 1];
    const nextIdx = (typeof last.index === "number" ? last.index : baseRows.length - 1) + 1;
    return [
      ...baseRows,
      {
        label: "next", index: nextIdx, time: null, date: null,
        CycleSerialNumber: nextIdx,
        _actualFieldKey: col,
        actual: null, lstm: v, target,
      },
    ];
  })();

  const lstmPointCount = combined.filter(d => d.lstm != null).length;
  const capped = tsData.length > slicedRows.length;
  const plotTag = rangeTag(slicedRows, capped ? ` (latest ${slicedRows.length} of ${tsData.length})` : "");

  // One-time console hint if the model line still has no data
  useEffect(() => {
    if (!predictions) return;
    if (!combined.some(d => d.lstm != null)) {
      console.warn(
        `[AI Controller] No Deep Sequence NN values found for "${col}". Response keys:`,
        Object.keys(predictions), "First item:", predList[0]
      );
    }
  }, [predictions, predSeries, col]);

  useEffect(() => {
    if (!combined.length) return;
    const timers = [];

    const toFetch = combined.filter(d => d.actual != null).slice(0, 20);

    toFetch.forEach((row, i) => {
      const t = setTimeout(() => {
        const val = row.actual;
        if (val == null) return;

        const rounded  = Math.round(val * 10) / 10;
        const cycleNum = row.CycleSerialNumber ?? row.index ?? i;
        const ctx = {};
        Object.entries(row).forEach(([k, v]) => {
          if (typeof v === "number" && !k.startsWith("_") && k !== "index")
            ctx[k] = Math.round(v * 100) / 100;
        });
        if (cycleNum != null) ctx.cycle_num = cycleNum;
        const fields = [
          {
            field:  col,
            label:  FIELD_META[col]?.label || col,
            value:  rounded,
            unit,
            status: row._actualStatus || undefined,
          },
        ];
        const vel = row.ArrivalVelocity;
        if (vel != null) {
          fields.push({
            field:  "ArrivalVelocity",
            label:  "Plunger Arrival Velocity",
            value:  Math.round(vel * 100) / 100,
            unit:   "ft/min",
            status: vel < 350  ? "WARNING (Too Slow: Plunger May Drop Back)"
                  : vel > 1200 ? "CRITICAL (Too Fast: Excess Energy)"
                  :              "GOOD (Optimal Velocity Range 350-1200 ft/min)",
          });
        }

        bgPrefetch(col, rounded, unit, cycleNum, ctx, fields);
      }, i * 200); 

      timers.push(t);
    });

    return () => timers.forEach(clearTimeout);
  }, [combined.length, col, unit]); 

  const tabs = [
    { id:"tp", label:"TP (Tubing Pressure)"   },
    { id:"cp", label:"CP (Casing Pressure)"   },
    { id:"pr", label:"PR (Production Rate)" },
  ];

  const pairs      = combined.filter(d=>d.actual!=null && d.lstm!=null);
  const mae        = pairs.length ? pairs.reduce((s,d)=>s+Math.abs(d.actual-d.lstm),0)/pairs.length : null;
  const actuals    = combined.filter(d=>d.actual!=null);
  const gap        = actuals.length && target ? actuals.reduce((s,d)=>s+(target-d.actual),0)/actuals.length : null;
  const modeValues = tsData.map(d=>d[col]).filter(v=>v!=null&&!isNaN(v));
  const modeMin    = modeValues.length ? Math.min(...modeValues).toFixed(2) : "-";
  const modeMax    = modeValues.length ? Math.max(...modeValues).toFixed(2) : "-";
  const modeAvg    = modeValues.length ? (modeValues.reduce((a,b)=>a+b,0)/modeValues.length).toFixed(2) : "-";
  const modeStd    = modeValues.length > 1
    ? Math.sqrt(modeValues.reduce((s,v)=>s+Math.pow(v-(modeValues.reduce((a,b)=>a+b)/modeValues.length),2),0)/modeValues.length).toFixed(2) : "-";

  return (
    <Card accent={color} style={{ marginBottom:18 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, flexWrap:"wrap", gap:10 }}>
        <SectionTitle tag={plotTag} sub={`Showing: ${nameMap[mode]}: switch mode above or use tabs below`}>
          Actual vs. Target, {nameMap[mode]}
        </SectionTitle>
        <TabBtns options={tabs} value={mode} onChange={onModeChange} accent={color}/>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:14 }}>
        {[
          { label:"MEAN",    val:modeAvg, color },
          { label:"MIN",     val:modeMin, color:C.green  },
          { label:"MAX",     val:modeMax, color:C.red    },
          { label:"STD DEV", val:modeStd, color:C.purple },
        ].map(s => (
          <div key={s.label} style={{ background:C.panel, borderRadius:8, padding:"10px 12px", textAlign:"center" }}>
            <div style={{ fontFamily:MONO, fontSize:8, color:C.muted, letterSpacing:1, marginBottom:4 }}>{s.label}</div>
            <div style={{ fontFamily:MONO, fontSize:16, fontWeight:700, color:s.color }}>{s.val}</div>
            <div style={{ fontFamily:MONO, fontSize:9, color:C.muted }}>{unit}</div>
          </div>
        ))}
      </div>
      {capped && (
        <div style={{ fontFamily:MONO, fontSize:9, color:C.muted, marginBottom:10 }}>
          Chart shows all cycles in the window ({rangeTag(tsData).split(" · ")[0]}).
        </div>
      )}
      <div style={{ height:304 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={combined} margin={{ top:5, right:10, left:-10, bottom:0 }}>
            <defs>
              <linearGradient id={`gAV_${mode}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor={color} stopOpacity={0.3}/>
                <stop offset="95%" stopColor={color} stopOpacity={0.02}/>
              </linearGradient>
            </defs>
            <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
            <XAxis dataKey="index" tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} interval="preserveStartEnd" minTickGap={24} tickFormatter={cycleTickFmt} label={CYCLE_XAXIS_LABEL}/>
            {dateXAxis(combined, 5)}
            <YAxis tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false}/>
            <Tooltip {...TTIP_PROPS}/>
            <Legend wrapperStyle={{ fontFamily:MONO, fontSize:10 }}/>
            {target && (
              <ReferenceLine y={target} stroke={C.pink} strokeDasharray="6 3"
                label={{ value:`Target ${target.toFixed(0)} ${unit}`, fill:C.pink, fontSize:9, fontFamily:MONO, position:"right" }}/>
            )}
            <Area type="monotone" dataKey="actual" name={`${mode.toUpperCase()} Actual`}
              stroke={color} fill={`url(#gAV_${mode})`} strokeWidth={2}
              dot={{ r:3, fill:color, stroke:"#fff", strokeWidth:1 }} connectNulls/>
            <Line type="monotone" dataKey="lstm" name="AI model"
              stroke={C.purple} strokeWidth={2} strokeDasharray="5 3"
              dot={lstmPointCount <= 3 ? { r:4, fill:C.purple, stroke:"#fff", strokeWidth:1 } : false}
              connectNulls isAnimationActive={false}/>
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div style={{ display:"flex", gap:20, marginTop:10, flexWrap:"wrap" }}>
        {[
          { label:"Data Points",       val: `${actuals.length} cycles`,                       color:C.cyan },
          { label:"Dataset Average",   val: `${modeAvg} ${unit}`,                             color },
        ].map(s => (
          <div key={s.label} style={{ fontFamily:MONO, fontSize:10 }}>
            <span style={{ color:C.muted }}>{s.label}: </span>
            <span style={{ color:s.color, fontWeight:700 }}>{s.val}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

/* VELOCITY SAFE BAND*/
function VelocitySafeBand({ filteredData, velMin=350, velMax=1200 }) {
  const tsData = (filteredData || []).filter(d => d.ArrivalVelocity != null);
  const data = tsData.map((d, i) => {
    const vel  = d.ArrivalVelocity;
    const zone = vel < velMin ? "low" : vel > velMax ? "high" : "normal";
    return {
      ...d,                                               
      label: d.time || String(d.index ?? i),
      index: d.index ?? i,
      time:  d.time  || null,
      date:  d.date  || null,
      CycleSerialNumber: d.CycleSerialNumber ?? d.index ?? i,
      ArrivalVelocity: vel,
      velocity: vel,
      zone,
      _zoneStatus: zone === "low"    ? "WARNING"  :      
                   zone === "high"   ? "CRITICAL" :     
                   "GOOD",                               
    };
  });
  const counts = {
    low:    data.filter(d => d.zone === "low").length,
    normal: data.filter(d => d.zone === "normal").length,
    high:   data.filter(d => d.zone === "high").length,
  };

  return (
    <Card accent={C.amber} style={{ marginBottom:18 }}>
      <SectionTitle tag={rangeTag(data)} sub={`Low <${velMin} ft/min (pink/WARNING) | Normal ${velMin}-${velMax} ft/min (teal/GOOD) | High >${velMax} ft/min (amber/CRITICAL)`}>
        Velocity with Safe Band
      </SectionTitle>
      <div style={{ height:264 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top:5, right:10, left:-10, bottom:0 }}>
            <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
            <XAxis dataKey="index" tick={{ fill:C.muted, fontSize:8, fontFamily:MONO }} tickLine={false} interval="preserveStartEnd" minTickGap={24} tickFormatter={cycleTickFmt} label={CYCLE_XAXIS_LABEL}/>
            {dateXAxis(data, 5)}
            <YAxis tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false}/>
            <Tooltip {...TTIP_PROPS}/>
            <ReferenceLine y={velMin} stroke={`${C.pink}99`}  strokeDasharray="4 2" label={{ value:`Min ${velMin}: WARNING below`,  fill:C.pink,  fontSize:8, fontFamily:MONO }}/>
            <ReferenceLine y={velMax} stroke={`${C.amber}99`} strokeDasharray="4 2" label={{ value:`Max ${velMax}: CRITICAL above`, fill:C.amber, fontSize:8, fontFamily:MONO }}/>
            <Bar dataKey="ArrivalVelocity" name="Plunger Arrival Velocity" radius={[2,2,0,0]}>
              {data.map((entry, i) => (
                <Cell
                  key={i}
                  fill={entry.zone === "low" ? C.pink : entry.zone === "high" ? C.amber : C.teal}
                  fillOpacity={0.85}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div style={{ display:"flex", gap:12, marginTop:12 }}>
        {[
          { label:`Low < ${velMin}`,  count:counts.low,    color:C.pink,  badge:"WARNING",  desc:"Too slow: plunger may drop back",
            icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg> },
          { label:"Normal",           count:counts.normal, color:C.teal,  badge:"GOOD",     desc:"Optimal velocity range",
            icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="8 12 11 15 16 9"/></svg> },
          { label:`High > ${velMax}`, count:counts.high,   color:C.amber, badge:"CRITICAL", desc:"Too fast: excess energy",
            icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg> },
        ].map(s => (
          <div key={s.label} style={{ flex:1, textAlign:"center", background:`${s.color}0a`, border:`1px solid ${s.color}33`, borderRadius:8, padding:"10px 4px" }}>
            <div style={{ display:"flex", justifyContent:"center", color:s.color, marginBottom:3 }}>{s.icon}</div>
            <div style={{ fontFamily:MONO, fontSize:9, fontWeight:700, color:s.color, marginBottom:2 }}>{s.badge}</div>
            <div style={{ fontFamily:MONO, fontSize:20, fontWeight:700, color:s.color }}>{s.count}</div>
            <div style={{ fontFamily:MONO, fontSize:9, color:C.muted, marginBottom:2 }}>{s.label}</div>
            <div style={{ fontFamily:MONO, fontSize:8, color:s.color, opacity:0.7 }}>{s.desc}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}
function GradTooltip({ active, payload, coordinate, chartAreaRef }) {
  const { advice, loading, fetchAdvice, clearAdvice } = useAIAdvice();
  const prevKeyRef = useRef(null);

  const raw      = active && payload?.length ? payload[0]?.payload || {} : {};
  const cycleNum = raw.CycleSerialNumber ?? raw.cycle ?? raw.index ?? null;
  const timeStr  = raw.time || raw.timestamp || raw.date || null;
  const fieldKey = raw._actualFieldKey || null;
  let primaryField = null;
  let primaryValue = null;
  let primaryUnit  = "";

  if (active && payload?.length) {
    for (const p of payload) {
      if (primaryField !== null) break;
      const rawKey    = p.dataKey || p.name || "";
      const isGeneric = GENERIC_KEYS.has(rawKey);
      const lookupKey = isGeneric && fieldKey ? fieldKey : rawKey;
      const meta      = FIELD_META[lookupKey] || FIELD_META[rawKey];
      if (!meta) continue;
      primaryField = lookupKey;
      primaryUnit  = meta.unit || "";
      primaryValue = (isGeneric && fieldKey && typeof raw[lookupKey] === "number")
        ? raw[lookupKey]
        : (typeof p.value === "number" ? p.value : null);
    }
  }

  const context = {};
  Object.entries(raw).forEach(([k, v]) => {
    if (typeof v === "number" && !k.startsWith("_") && k !== "index") {
      context[k] = Math.round(v * 100) / 100;
    }
  });

  useEffect(() => {
    if (!active || !primaryField || primaryValue == null) return;
    const allFields = [];
    if (payload?.length) {
      for (const p of payload) {
        if (p.value == null) continue;
        const rawKey = p.dataKey || p.name || "";
        const isGeneric = GENERIC_KEYS.has(rawKey);
        const lookupKey = isGeneric && fieldKey ? fieldKey : rawKey;
        const meta = FIELD_META[lookupKey] || FIELD_META[rawKey];
        const val = typeof p.value === "number" ? p.value : null;
  
        if (val !== null) {
          allFields.push({
            field: lookupKey,
            label: meta?.label || rawKey,
            value: Math.round(val * 100) / 100,
            unit: meta?.unit || "",
          });
        }
      }
    }
    if (raw.ShutInGradient_psi_per_ft != null) {
      allFields.push({
        field: 'ShutInGradient',
        label: 'ShutIn Gradient',
        value: raw.ShutInGradient_psi_per_ft,
        unit: 'psi/ft'
      });
    }
  
    if (raw.AfterFlowGradient_psi_per_ft != null) {
      allFields.push({
        field: 'AfterFlowGradient',
        label: 'AfterFlow Gradient',
        value: raw.AfterFlowGradient_psi_per_ft,
        unit: 'psi/ft'
      });
    }
  
    if (raw.CsgAtOpen != null) {
      allFields.push({
        field: 'CsgAtOpen',
        label: 'Casing at Open',
        value: raw.CsgAtOpen,
        unit: 'psi'
      });
    }
  
    if (raw.TbgAtOpen != null) {
      allFields.push({
        field: 'TbgAtOpen',
        label: 'Tubing at Open',
        value: raw.TbgAtOpen,
        unit: 'psi'
      });
    }
  
    if (raw.LineAtOpen != null) {
      allFields.push({
        field: 'LineAtOpen',
        label: 'Line at Open',
        value: raw.LineAtOpen,
        unit: 'psi'
      });
    }
    if (cycleNum != null) {
      allFields.unshift({
        field: 'Cycle',
        label: 'Cycle',
        value: cycleNum,
        unit: ''
      });
    }
  
    const rounded = typeof primaryValue === "number"
      ? Math.round(primaryValue * 10) / 10 : primaryValue;
    const allValsKey = allFields.map(f => `${f.field}:${f.value}`).join("|");
    const key = `${primaryField}_${rounded}_${allValsKey}`;
    if (prevKeyRef.current === key) return;
    prevKeyRef.current = key;
    fetchAdvice(primaryField, rounded, primaryUnit, cycleNum, context, allFields);
  }, [active, primaryField, primaryValue, payload, fieldKey, raw, cycleNum, context, fetchAdvice]);

  useEffect(() => {
    if (!active) {
      clearAdvice();
    }
  }, [active, clearAdvice]);

  if (!active || !payload?.length) return null;
  const rect = chartAreaRef?.current?.getBoundingClientRect();
  if (!rect || !coordinate) return null;

  const TIP_W = 300;
  const TIP_H = 260;
  const GAP   = 10;
  const vw    = window.innerWidth;
  let left = rect.left + coordinate.x - TIP_W / 2;
  let top  = rect.top - TIP_H - GAP;
  if (left < 6)               left = 6;
  if (left + TIP_W > vw - 6)  left = vw - TIP_W - 6;
  if (top  < 6)               top  = rect.bottom + GAP; 

  return ReactDOM.createPortal(
    <div style={{
      position: "fixed", left, top,
      background: C.card,
      border: `1px solid ${C.border2}`,
      borderRadius: 12,
      padding: "12px 16px",
      fontFamily: MONO,
      fontSize: 11,
      boxShadow: "0 12px 40px #000e",
      maxWidth: TIP_W,
      zIndex: 99999,
      pointerEvents: "none",
    }}>
      <div style={{ borderBottom: `1px solid ${C.border}`, paddingBottom: 7, marginBottom: 8 }}>
        {cycleNum != null && (
          <div style={{ color: C.cyan, fontWeight: 700, fontSize: 12, marginBottom: 2 }}>C{cycleNum}</div>
        )}
        {timeStr && <div style={{ color: C.muted, fontSize: 9 }}>{timeStr}</div>}
      </div>
      {payload.map((p, i) => {
        const rawKey    = p.dataKey || p.name || "";
        const isGeneric = GENERIC_KEYS.has(rawKey);
        const lookupKey = isGeneric && fieldKey ? fieldKey : rawKey;
        const meta      = FIELD_META[lookupKey] || FIELD_META[rawKey];
        let displayName;
        if      (rawKey === "c1_pos") displayName = "Shut-In → +Gradient";
        else if (rawKey === "c1_neg") displayName = "Shut-In → −Gradient";
        else if (rawKey === "c2_pos") displayName = "After-Flow → +Gradient";
        else if (rawKey === "c2_neg") displayName = "After-Flow → −Gradient";
        else                          displayName = meta?.label || p.name || rawKey;
        const unit      = meta?.unit || "";
        const formatted = typeof p.value === "number"
          ? (Math.abs(p.value) >= 100 ? p.value.toFixed(1) : p.value.toFixed(3))
          : String(p.value ?? "-");
        return (
          <div key={i} style={{
            marginBottom: 4, paddingBottom: 4,
            borderBottom: i < payload.length - 1 ? `1px solid ${C.border}` : "none",
          }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12 }}>
              <div style={{ color:C.muted, fontSize:10, flex:1, lineHeight:1.3 }}>{displayName}</div>
              <div style={{ textAlign:"right", flexShrink:0 }}>
                <span style={{ color:p.color || C.cyan, fontWeight:700, fontSize:12 }}>{formatted}</span>
                {unit && <span style={{ color:C.muted, fontSize:9, marginLeft:3 }}>{unit}</span>}
              </div>
            </div>
          </div>
        );
      })}
      <div style={{
        marginTop: 8, paddingTop: 8,
        borderTop: `1px solid ${C.border}`,
        fontSize: 10, lineHeight: 1.6,
        background: loading ? `${C.muted}08` : `${C.cyan}08`,
        borderRadius: 6, padding: "6px 8px",
      }}>
        {loading
          ? <span style={{ color:C.muted }}>Getting AI recommendation...</span>
          : advice
            ? renderAdvice(advice)
            : <span style={{ color:C.muted }}>Hover a data point for AI advice</span>
        }
      </div>
    </div>,
    document.body
  );
}

/* Chart body + Summary toggle for charts rendered inside their own component */
function ChartWithSummary({ height, areaRef, title, subtitle, accent, children }) {
  return (
    <>
      <div ref={areaRef} style={{ height }}>{children}</div>
      <PlotSummary inline title={title} subtitle={subtitle} accent={accent}>{children}</PlotSummary>
    </>
  );
}

function GradChart({ posKey, negKey, title, gradData, mode, onModeChange, tabs }) {
  const chartAreaRef = useRef(null);
  const tooltipEl = useMemo(() => <GradTooltip chartAreaRef={chartAreaRef} />, []);

  return (
    <div style={{ background:C.panel, borderRadius:10, padding:"14px 16px" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
        <div style={{ fontFamily:MONO, fontSize:11, color:C.text }}>{title}</div>
        <TabBtns options={tabs} value={mode} onChange={onModeChange} accent={C.teal}/>
      </div>
      <ChartWithSummary height={176} areaRef={chartAreaRef} accent={C.teal} title={title} subtitle={`Production response pattern across ${mode.toUpperCase()}`}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={gradData} margin={{ top:5, right:10, left:-15, bottom:0 }}>
            <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
            <XAxis dataKey="index" tick={{ fill:C.muted, fontSize:8, fontFamily:MONO }} tickLine={false} interval="preserveStartEnd" minTickGap={24} tickFormatter={cycleTickFmt}/>
            {dateXAxis(gradData, 4, true)}
            <YAxis tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false}/>
            <Tooltip
              content={tooltipEl}
              wrapperStyle={{
                visibility:"hidden", pointerEvents:"none",
                position:"fixed", top:0, left:0,
                width:0, height:0, overflow:"visible", zIndex:0,
              }}
              allowEscapeViewBox={{ x:true, y:true }}
              cursor={{ fill:"rgba(255,255,255,0.05)" }}
            />
            <ReferenceLine y={0} stroke={C.border2} strokeDasharray="4 2"/>
            <Bar dataKey={posKey} name="Positive Slopes" fill={C.teal} fillOpacity={0.85} radius={[2,2,0,0]}/>
            <Bar dataKey={negKey} name="Negative Slopes" fill={C.pink} fillOpacity={0.85} radius={[2,2,0,0]}/>
          </BarChart>
        </ResponsiveContainer>
      </ChartWithSummary>
    </div>
  );
}
function NeuralNetworkModel({ filteredData, mode, onModeChange }) {
  const tsData    = filteredData || [];
  const colMap    = { pr:"FlowRateAtClose", cp:"CsgAtOpen", tp:"TbgAtOpen" };
  const targetCol = colMap[mode];

  const gradData = tsData.slice(1).map((row, i) => {
    const prev    = tsData[i];
    const dTarget = (row[targetCol]    || 0) - (prev[targetCol]    || 0);
    const dC1     = (row.TimeOffTotal  || 0) - (prev.TimeOffTotal  || 0);
    const dC2     = (row.TimeAfterFlow || 0) - (prev.TimeAfterFlow || 0);
    const clamp   = (v, lim=15) => Math.max(-lim, Math.min(lim, v));
    return {
      label: row.time || String(row.index ?? i), index: row.index ?? i,
      time: row.time || null, date: row.date || null, _t: row._t,
      CycleSerialNumber: row.CycleSerialNumber ?? row.index ?? i,
      _actualFieldKey: targetCol,
      [targetCol]: row[targetCol], TimeOffTotal: row.TimeOffTotal, TimeAfterFlow: row.TimeAfterFlow,
      c1_pos: clamp(dC1>0 && dTarget>0 ?  Math.abs(dTarget/(dC1+1e-6)) : 0),
      c1_neg: clamp(dC1<0 && dTarget<0 ? -Math.abs(dTarget/(dC1+1e-6)) : 0),
      c2_pos: clamp(dC2>0 && dTarget>0 ?  Math.abs(dTarget/(dC2+1e-6)) : 0),
      c2_neg: clamp(dC2<0 && dTarget<0 ? -Math.abs(dTarget/(dC2+1e-6)) : 0),
    };
  }).slice(-60);

  const tabs = [{ id:"tp", label:"TP" }, { id:"cp", label:"CP" }, { id:"pr", label:"PR" }];

  return (
    <Card accent={C.purple} style={{ marginBottom:18 }}>
      <SectionTitle tag={rangeTag(gradData)} sub={`Production response patterns across ${mode.toUpperCase()}: C1=shut-in, C2=afterflow`}>
      Production Pattern Analysis
      </SectionTitle>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
        <GradChart posKey="c1_pos" negKey="c1_neg" title="PR→C1 Up"
          gradData={gradData} mode={mode} onModeChange={onModeChange} tabs={tabs}/>
        <GradChart posKey="c1_neg" negKey="c1_pos" title="PR→C1 Down"
          gradData={gradData} mode={mode} onModeChange={onModeChange} tabs={tabs}/>
        <GradChart posKey="c2_pos" negKey="c2_neg" title="PR→C2 Up"
          gradData={gradData} mode={mode} onModeChange={onModeChange} tabs={tabs}/>
        <GradChart posKey="c2_neg" negKey="c2_pos" title="PR→C2 Down"
          gradData={gradData} mode={mode} onModeChange={onModeChange} tabs={tabs}/>
      </div>
    </Card>
  );
}

function LearningWindowsAnalysis({ filteredData }) {
  const tsData = (filteredData || []).slice(-80);

  const windows = tsData.map((row, i) => {
    const vel  = row.ArrivalVelocity;
    const base = {
      label: row.time || String(row.index ?? i), index: row.index ?? i,
      time: row.time || null, date: row.date || null, _t: row._t,
      CycleSerialNumber: row.CycleSerialNumber ?? row.index ?? i,
      _actualFieldKey: "ArrivalVelocity", 
      ArrivalVelocity: vel,
      _velocityForTooltip: vel,
      _chartType: "learning_windows",
      _acceptIsGood: true,
      _abortIsBad: true,
    };
    if (vel == null)               return { ...base, accept: 0, abort:  0 };
    if (vel >= 200 && vel <= 1500) return { ...base, accept: 1, abort:  0 };
    if (vel < 100  || vel > 3000)  return { ...base, accept: 0, abort: -1 };
    return                                { ...base, accept: 0, abort:  0 };
  });

  const nAccept  = windows.filter(w => w.accept ===  1).length;
  const nAbort   = windows.filter(w => w.abort  === -1).length;
  const nNeither = windows.filter(w => w.accept === 0 && w.abort === 0).length;

  const abortData = tsData.map((row, i) => ({
    label: row.time || String(row.index ?? i), index: row.index ?? i,
    time: row.time || null, date: row.date || null, _t: row._t,
    CycleSerialNumber: row.CycleSerialNumber ?? row.index ?? i,
    _actualFieldKey: "ArrivalVelocity", 
    ArrivalVelocity: row.ArrivalVelocity,
    _chartType: "abort_reasons",
    _reasonIsBad: true,
    reason: row.ArrivalVelocity == null ? -2
      : (row.ArrivalVelocity < 100 || row.ArrivalVelocity > 3000) ? -1 : 0,
  }));

  return (
    <Card accent={C.blue} style={{ marginBottom:18 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <SectionTitle tag={rangeTag(windows)} sub={`Model Learning (1=Accept, -1=Abort, 0=Neither)`}>
          Learning Windows Analysis
        </SectionTitle>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:16 }}>
      {[
        {
          label:"Accept (1)", count:nAccept, color:C.teal, badge:"GOOD",
          icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="8 12 11 15 16 9"/></svg>,
          desc:"Velocity within valid range"
        },
        {
          label:"Abort (-1)", count:nAbort, color:C.red, badge:"CRITICAL",
          icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>,
          desc:"Velocity outside safe bounds"
        },
        {
          label:"Neither (0)", count:nNeither, color:C.muted, badge:"NORMAL",
          icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="8" y1="12" x2="16" y2="12"/></svg>,
          desc:"Outside learning window range"
        },
      ].map(s => (
        <div key={s.label} style={{ background:C.panel, borderRadius:8, padding:"12px 14px", textAlign:"center", border:`1px solid ${s.color}33` }}>
          <div style={{ display:"flex", justifyContent:"center", color:s.color, marginBottom:4 }}>{s.icon}</div>
          <div style={{ fontFamily:MONO, fontSize:9, fontWeight:700, color:s.color, marginBottom:2 }}>{s.badge}</div>
          <div style={{ fontFamily:MONO, fontSize:22, fontWeight:700, color:s.color }}>{s.count}</div>
          <div style={{ fontFamily:MONO, fontSize:9, color:C.muted, marginTop:2 }}>{s.label}</div>
          <div style={{ fontFamily:MONO, fontSize:8, color:s.color, opacity:0.7, marginTop:2 }}>{s.desc}</div>
        </div>
      ))}
      </div>
      <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, marginBottom:8 }}>Accept/Abort Learning Windows</div>
      <div style={{ height:224, marginBottom:18 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={windows} margin={{ top:5, right:10, left:-15, bottom:0 }}>
            <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
            <XAxis dataKey="index" tick={{ fill:C.muted, fontSize:8, fontFamily:MONO }} tickLine={false} interval="preserveStartEnd" minTickGap={24} tickFormatter={cycleTickFmt} label={CYCLE_XAXIS_LABEL}/>
            {dateXAxis(windows, 5)}
            <YAxis domain={[-1,1]} ticks={[-1,0,1]}
              tickFormatter={v => v===1?"Accept":v===-1?"Abort":"Neither"}
              tick={{ fill:C.muted, fontSize:8, fontFamily:MONO }} tickLine={false} axisLine={false}/>
            <Tooltip {...TTIP_PROPS}/>
            <Legend wrapperStyle={{ fontFamily:MONO, fontSize:10 }}/>
            <ReferenceLine y={0} stroke={C.border2}/>
            <Bar dataKey="accept" name="Accept" fill={C.teal} fillOpacity={0.85} radius={[2,2,0,0]}/>
            <Bar dataKey="abort"  name="Abort"  fill={C.red}  fillOpacity={0.85} radius={[2,2,0,0]}/>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, marginBottom:8 }}>Abort Reasons Distribution</div>
      <div style={{ height:156 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={abortData} margin={{ top:5, right:10, left:-15, bottom:0 }}>
            <XAxis dataKey="index" tick={{ fill:C.muted, fontSize:8, fontFamily:MONO }} tickLine={false} interval="preserveStartEnd" minTickGap={24} tickFormatter={cycleTickFmt}/>
            {dateXAxis(abortData, 5, true)}
            <YAxis tick={{ fill:C.muted, fontSize:8, fontFamily:MONO }} tickLine={false} axisLine={false}/>
            <Tooltip {...TTIP_PROPS}/>
            <Bar dataKey="reason" name="Abort Reason Code" radius={[2,2,0,0]}>
              {abortData.map((e,i) => <Cell key={i} fill={e.reason < 0 ? C.red : C.muted2} fillOpacity={0.7}/>)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function DiagnosticsPanel({ filteredData }) {
  const [hoveredMode, setHoveredMode] = useState(null);
  const tsData = (filteredData || []).slice(-80);

  let seed = 42;
  const prng = () => {
    seed = (seed * 1664525 + 1013904223) & 0xffffffff;
    return (seed >>> 0) / 0xffffffff;
  };

  const modesData = tsData.map((row, i) => {
    const vel = row.ArrivalVelocity;
    const lf  = row.LoadFactor;
    let rescue = 0, tour = 0, opt = 0;
    if      (vel != null && (vel < 300 || vel > 1500)) { rescue = 1; }
    else if (lf  != null && lf  > 0.4)                { rescue = 1; }
    else if (prng() > 0.35)                            { tour   = 1; }
    else                                               { opt    = 1; }
    return {
      label: row.time || String(row.index ?? i), index: row.index ?? i,
      time: row.time || null, date: row.date || null, _t: row._t,
      CycleSerialNumber: row.CycleSerialNumber ?? row.index ?? i,
      _actualFieldKey: "ArrivalVelocity",
      ArrivalVelocity: row.ArrivalVelocity, LoadFactor: row.LoadFactor,
      _chartType: "controller_modes",
      _tourIsGood: true,
      _rescueIsBad: true,
      _optIsGood: true,
      "TOUR+HOLD": tour, RESCUE: rescue, PR_OPT: opt,
    };
  });

  const totals = {
    tour:   modesData.filter(d => d["TOUR+HOLD"] === 1).length,
    rescue: modesData.filter(d => d.RESCUE        === 1).length,
    opt:    modesData.filter(d => d.PR_OPT         === 1).length,
  };

  const activeData = tsData.map((row, i) => ({
    label: row.time || String(row.index ?? i), index: row.index ?? i,
    time: row.time || null, date: row.date || null, _t: row._t,
    CycleSerialNumber: row.CycleSerialNumber ?? row.index ?? i,
    _actualFieldKey: "ArrivalVelocity", 
    ArrivalVelocity: row.ArrivalVelocity,
    _chartType: "controller_active_status",
    _activeIsGood: true,
    _inactiveIsBad: true,
    active:   row.ArrivalVelocity != null && row.ArrivalVelocity > 100 ? 1 : 0,
    inactive: row.ArrivalVelocity == null || row.ArrivalVelocity <= 100 ? 1 : 0,
  }));

  return (
    <Card accent={C.teal} style={{ marginBottom:18 }}>
      <SectionTitle tag={rangeTag(tsData)} sub={`TOUR+HOLD = learning | PR_OPT = optimizing | RESCUE = performance optimization`}>
        Diagnostics: Controller Modes Distribution
      </SectionTitle>
      <div style={{ height:156, marginBottom:18 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={activeData} margin={{ top:5, right:20, left:-15, bottom:0 }}>
            <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
            <XAxis dataKey="index" tick={{ fill:C.muted, fontSize:8, fontFamily:MONO }} tickLine={false} interval="preserveStartEnd" minTickGap={24} tickFormatter={cycleTickFmt} label={CYCLE_XAXIS_LABEL}/>
            {dateXAxis(activeData, 5, true)}
            <YAxis tick={{ fill:C.muted, fontSize:8, fontFamily:MONO }} tickLine={false} axisLine={false}/>
            <Tooltip {...TTIP_PROPS}/>
            <Legend wrapperStyle={{ fontFamily:MONO, fontSize:9 }}/>
            <Bar dataKey="active"   name="Active (1)"   stackId="a" fill={C.teal}   fillOpacity={0.85} radius={[0,0,0,0]}/>
            <Bar dataKey="inactive" name="Inactive (0)" stackId="a" fill={C.muted2} fillOpacity={0.6}  radius={[2,2,0,0]}/>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div style={{ height:224 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={modesData} margin={{ top:5, right:20, left:-15, bottom:0 }}>
            <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
            <XAxis dataKey="index" tick={{ fill:C.muted, fontSize:8, fontFamily:MONO }} tickLine={false} interval="preserveStartEnd" minTickGap={24} tickFormatter={cycleTickFmt} label={CYCLE_XAXIS_LABEL}/>
            {dateXAxis(modesData, 5)}
            <YAxis tick={{ fill:C.muted, fontSize:9, fontFamily:MONO }} tickLine={false} axisLine={false}/>
            <Tooltip {...TTIP_PROPS}/>
            <Legend wrapperStyle={{ fontFamily:MONO, fontSize:9 }}/>
            <Bar dataKey="TOUR+HOLD" name="TOUR+HOLD" stackId="m" fill={C.teal}  fillOpacity={0.85} radius={[0,0,0,0]}/>
            <Bar dataKey="RESCUE"    name="RESCUE"    stackId="m" fill={C.orange} fillOpacity={0.85} radius={[0,0,0,0]}/>
            <Bar dataKey="PR_OPT"    name="PR_OPT"    stackId="m" fill={C.amber} fillOpacity={0.85} radius={[2,2,0,0]}/>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div style={{ display:"flex", gap:12, marginTop:12 }}>
        {[
          {
            label:"TOUR+HOLD",
            count:totals.tour,
            color:C.teal,
            description:"Learning mode: observes cycle behavior and learns from operating data.",
          },
          {
            label:"RESCUE",
            count:totals.rescue,
            color:C.orange,
            description:"Corrective mode: brings velocity or load conditions back toward safe limits.",
          },
          {
            label:"PR_OPT",
            count:totals.opt,
            color:C.amber,
            description:"Production optimization mode: adjusts operation to improve production performance.",
          },
        ].map(s => (
          <div
            key={s.label}
            onMouseEnter={() => setHoveredMode(s.label)}
            onMouseLeave={() => setHoveredMode(null)}
            style={{
              flex:1,
              position:"relative",
              textAlign:"center",
              background:`${s.color}0a`,
              border:`1px solid ${s.color}33`,
              borderRadius:8,
              padding:"8px 4px",
              cursor:"help",
            }}
          >
            {hoveredMode === s.label && (
              <div
                data-mode-tooltip
                style={{
                  display:"block",
                  position:"absolute",
                  left:"50%",
                  bottom:"calc(100% + 8px)",
                  transform:"translateX(-50%)",
                  width:260,
                  maxWidth:"calc(100vw - 40px)",
                  padding:"9px 12px",
                  background:C.card,
                  border:`1px solid ${s.color}66`,
                  borderRadius:7,
                  boxShadow:"0 8px 24px #000b",
                  color:C.text,
                  fontFamily:MONO,
                  fontSize:10,
                  lineHeight:1.5,
                  textAlign:"left",
                  zIndex:100,
                  pointerEvents:"none",
                }}
              >
                {s.description}
              </div>
            )}
            <div style={{ fontFamily:MONO, fontSize:20, fontWeight:700, color:s.color }}>{s.count}</div>
            <div style={{ fontFamily:MONO, fontSize:9, color:C.muted }}>{s.label}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function ParametersModal({ onClose, modelInfo, shutinData, afterflowData, wellSpecs, setpoints }) {

  const inputFeatures  = modelInfo?.input_features  || [];
  const targetFeatures = modelInfo?.target_features || [];

  const sp0 = setpoints?.controller_status === "OK" ? setpoints : setpoints?.historical_reference;
  const spMetrics = setpoints?.metrics || {};
  const spDiag    = setpoints?.cycle_diagnostics || {};
  const sp = {
    production_rate_target_mcfd: sp0?.production_rate_target_mcfd ?? spMetrics.FlowRateAtClose?.mean,
    casing_pressure_target_psi:  sp0?.casing_pressure_target_psi  ?? spMetrics.CsgAtOpen?.mean,
    tubing_over_static_psi:      sp0?.tubing_over_static_psi      ?? spDiag.tubing_over_static_applied_psi,
    afterflow_min_flowrate_mcfd: sp0?.afterflow_min_flowrate_mcfd ?? spDiag.afterflow_minimum_applied_mcfd,
    avg_arrival_velocity_ftmin:  sp0?.avg_arrival_velocity_ftmin  ?? spMetrics.ArrivalVelocity?.mean,
    avg_shutin_duration_min:     sp0?.avg_shutin_duration_min     ?? spMetrics.TimeOffTotal?.mean,
    avg_afterflow_duration_min:  sp0?.avg_afterflow_duration_min  ?? spMetrics.TimeAfterFlow?.mean,
    avg_load_factor:             sp0?.avg_load_factor             ?? spMetrics.LoadFactor?.mean,
  };

  const prTarget   = sp?.production_rate_target_mcfd;
  const cpTarget   = sp?.casing_pressure_target_psi;
  const tubingOver = sp?.tubing_over_static_psi;
  const afMinFlow  = sp?.afterflow_min_flowrate_mcfd;
  const avgVel     = sp?.avg_arrival_velocity_ftmin;
  const avgShutIn  = sp?.avg_shutin_duration_min;
  const avgAfFlow  = sp?.avg_afterflow_duration_min;
  const avgLF      = sp?.avg_load_factor;

  const avgCsgOpen   = shutinData?.summary?.CsgAtOpen?.mean;
  const avgLiqHeight = shutinData?.summary?.LiquidHeight_ft?.mean;
  // Buildup rate is always shown as a positive number
  const avgBuildupRaw = shutinData?.summary?.BuildupRate_psi_per_min?.mean;
  const avgBuildup    = avgBuildupRaw != null ? Math.abs(Number(avgBuildupRaw)) : null;

  const wellDepth   = wellSpecs?.WellDepth_feet;
  const tubingID    = wellSpecs?.TubingID_inches;
  const casingID    = wellSpecs?.CasingID_inches;
  const casingDepth = wellSpecs?.CasingDepth_feet;
  const eotTVD      = wellSpecs?.EOTTVD_feet;

  const fmt = (v, dec = 1) => (v != null && v !== "-" && Number.isFinite(Number(v))) ? Number(v).toFixed(dec) : "-";
  const SLATE_LABEL = "#94a3b8";
  const SLATE_UNIT  = "#64748b";
  const STORAGE_KEY = "plunger_controller_params";
  const [edits, setEdits] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); } catch { return {}; }
  });
  const [editing, setEditing] = useState(false);
  const ALL_KEYS = ["cp_target","tubing_over","af_min_flow","avg_vel","avg_shutin","avg_afterflow","avg_lf","well_depth","tubing_id","casing_id","casing_depth","eot_tvd"];

  function val(key, fallback) {
    return edits[key] !== undefined ? edits[key] : fallback;
  }
  function changed(key, fallback) {
    return edits[key] !== undefined && String(edits[key]) !== String(fallback);
  }
  function set(key, v) { setEdits(prev => ({ ...prev, [key]: v })); }
  function saveAll()  {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(edits)); } catch {}
    setEditing(false);
  }
  function resetAll() {
    const next = { ...edits };
    ALL_KEYS.forEach(k => delete next[k]);
    setEdits(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
  }

  /* NOTE: these two are called as plain functions (not <Components/>) so inputs keep focus while typing */
  function renderRow({ label, paramKey, fallback, unit = "", color, readOnly = false }) {
    const isEditing = editing && !readOnly;
    const isChanged = changed(paramKey, fallback);
    return (
      <div key={paramKey} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: `1px solid ${C.border}`, fontFamily: MONO, fontSize: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {isChanged && <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.amber, flexShrink: 0 }} />}
          <span style={{ color: SLATE_LABEL }}>{label}</span>
        </div>
        {isEditing ? (
          <input
            type="number"
            value={val(paramKey, fallback) ?? ""}
            onChange={e => set(paramKey, e.target.value)}
            style={{
              width: 80, padding: "2px 8px", background: C.bg, border: `1px solid ${C.cyan}99`,
              borderRadius: 4, color: C.text, fontFamily: MONO, fontSize: 10, textAlign: "right", outline: "none",
            }}
          />
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 4, fontWeight: 700, color: color || (isChanged ? C.amber : C.text) }}>
            <span>{fmt(val(paramKey, fallback))}</span>
            {unit && <span style={{ color: SLATE_UNIT, fontWeight: 400, fontSize: 9 }}>{unit}</span>}
          </div>
        )}
      </div>
    );
  }

  function renderGroupHeader({ title, color }) {
    return (
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 8, marginBottom: 8, borderBottom: `1px solid ${C.border2}` }}>
        <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 12, color }}>{title}</div>
      </div>
    );
  }

  const topBtn = (extra) => ({
    display: "flex", alignItems: "center", gap: 4, padding: "5px 12px", borderRadius: 6,
    fontFamily: MONO, fontSize: 10, cursor: "pointer", ...extra,
  });

  const groupBox = { background: C.panel, borderRadius: 12, padding: 16, border: `1px solid ${C.border}` };

  return (
    <div style={{ position: "fixed", inset: 0, background: "#000c", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, width: "100%", maxWidth: 896, maxHeight: "85vh", overflowY: "auto", padding: 24, boxShadow: "0 25px 50px -12px #000a", position: "relative" }}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 16, marginBottom: 16, borderBottom: `1px solid ${C.border}` }}>
          <div>
            <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 16, color: C.text }}>Controller Parameters & Model Specs</div>
            <div style={{ fontFamily: MONO, fontSize: 10, color: SLATE_LABEL, marginTop: 2 }}>
              SCADA setpoints, model architecture, and well physical constraints
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {editing ? (
              <>
                <button onClick={saveAll} style={topBtn({ background: `${C.green}10`, border: `1px solid ${C.green}80`, color: C.green })}>✓ Save</button>
                <button onClick={resetAll} style={topBtn({ background: C.border, border: "1px solid transparent", color: SLATE_LABEL })}>↺ Reset</button>
              </>
            ) : (
              <button onClick={() => setEditing(true)} style={topBtn({ background: "transparent", border: `1px solid ${C.border2}`, color: SLATE_LABEL })}>✎ Edit</button>
            )}
          <button onClick={onClose} title="Close" style={{ background: "transparent", border: "none", color: SLATE_LABEL, fontSize: 20, lineHeight: 1, padding: "6px 10px", borderRadius: 8, cursor: "pointer" }}>✕</button>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 20 }}>

          {/* Group 1: Model Specs */}
          <div style={groupBox}>
            {renderGroupHeader({ title: `1. Input Features (${inputFeatures.length})`, color: C.purple })}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div style={{ padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {inputFeatures.map(f => (
                    <span key={f} style={{ fontFamily: MONO, fontSize: 8, background: C.border, color: "#cbd5e1", padding: "2px 6px", borderRadius: 4 }}>{f}</span>
                  ))}
                </div>
              </div>
              <div style={{ padding: "8px 0" }}>
                <div style={{ fontFamily: MONO, fontSize: 9, color: SLATE_UNIT, textTransform: "uppercase", marginBottom: 4 }}>Target Features ({targetFeatures.length})</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {targetFeatures.map(f => (
                    <span key={f} style={{ fontFamily: MONO, fontSize: 8, background: `${C.cyan}12`, color: C.cyan, padding: "2px 6px", borderRadius: 4, border: `1px solid ${C.cyan}33` }}>{f}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Group 2: Target Values & Setpoints */}
          <div style={groupBox}>
            {renderGroupHeader({ title: "2. Operating Targets & Setpoints", color: C.teal })}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {renderRow({ label: "Production Rate Target", paramKey: "pr_target",   fallback: prTarget,   unit: "MCF/d", color: C.teal, readOnly: true })}
              {renderRow({ label: "Casing Pressure", paramKey: "cp_target",   fallback: cpTarget,   unit: "psi",   color: C.cyan })}
              {renderRow({ label: "Tubing Over Static",     paramKey: "tubing_over", fallback: tubingOver, unit: "psi",   color: C.amber })}
              {renderRow({ label: "Afterflow Min Flowrate", paramKey: "af_min_flow", fallback: afMinFlow,  unit: "MCF/d", color: C.teal })}
            </div>
          </div>

          {/* Group 3: Benchmarks */}
          <div style={groupBox}>
            {renderGroupHeader({ title: "3. Cycle Operating Benchmarks", color: C.cyan })}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {renderRow({ label: "Avg Arrival Velocity",    paramKey: "avg_vel",       fallback: avgVel,       unit: "ft/min",  color: C.cyan })}
              {renderRow({ label: "Avg Shut-In Duration",    paramKey: "avg_shutin",    fallback: avgShutIn,    unit: "min",     color: C.amber })}
              {renderRow({ label: "Avg Afterflow Duration",  paramKey: "avg_afterflow", fallback: avgAfFlow,    unit: "min",     color: C.teal })}
              {renderRow({ label: "Avg Load Factor",         paramKey: "avg_lf",        fallback: avgLF,                         color: C.green })}
              {renderRow({ label: "Avg Csg At Open",         paramKey: "avg_csg_open",  fallback: avgCsgOpen,   unit: "psi",     color: C.cyan,  readOnly: true })}
              {renderRow({ label: "Avg Liquid Column Height",paramKey: "avg_liq_height",fallback: avgLiqHeight, unit: "ft",      color: C.blue,  readOnly: true })}
              {renderRow({ label: "Avg Buildup Rate",        paramKey: "avg_buildup",   fallback: avgBuildup,   unit: "psi/min", color: C.teal,  readOnly: true })}
            </div>
          </div>

          {/* Group 4: Well Specs */}
          <div style={groupBox}>
            {renderGroupHeader({ title: "4. Well Physical Specifications", color: C.amber })}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {renderRow({ label: "Well Total Depth",      paramKey: "well_depth",   fallback: wellDepth,   unit: "ft", color: C.amber })}
              {renderRow({ label: "Tubing Inner Diameter", paramKey: "tubing_id",    fallback: tubingID,    unit: "in", color: C.amber })}
              {renderRow({ label: "Casing Inner Diameter", paramKey: "casing_id",    fallback: casingID,    unit: "in", color: C.amber })}
              {renderRow({ label: "Casing Depth",          paramKey: "casing_depth", fallback: casingDepth, unit: "ft", color: C.amber })}
              {renderRow({ label: "End of Tubing (TVD)",   paramKey: "eot_tvd",      fallback: eotTVD,      unit: "ft", color: C.amber })}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
function renderAdvice(text) {
  if (!text) return null;
  const ICONS = {
    "◆": (
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}>
        <circle cx="6.5" cy="6.5" r="5" stroke="#22c55e" strokeWidth="1.4" fill="#22c55e22"/>
        <path d="M4 6.5L5.8 8.3L9 5" stroke="#22c55e" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    "▲": (
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}>
        <path d="M6.5 1.5L11.8 10.5H1.2L6.5 1.5Z" stroke="#f59e0b" strokeWidth="1.4" fill="#f59e0b22" strokeLinejoin="round"/>
        <line x1="6.5" y1="4.5" x2="6.5" y2="7.5" stroke="#f59e0b" strokeWidth="1.4" strokeLinecap="round"/>
        <circle cx="6.5" cy="9.2" r="0.8" fill="#f59e0b"/>
      </svg>
    ),
    "■": (
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}>
        <circle cx="6.5" cy="6.5" r="5" stroke="#ef4444" strokeWidth="1.4" fill="#ef444422"/>
        <path d="M4.5 4.5L8.5 8.5M8.5 4.5L4.5 8.5" stroke="#ef4444" strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
    ),
    "▽": (
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}>
        <path d="M6.5 2.5V10M3.5 7L6.5 10L9.5 7" stroke="#f87171" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    "▶": (
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}>
        <path d="M6.5 10.5V2.5M3.5 5.5L6.5 2.5L9.5 5.5" stroke="#f59e0b" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  };
  for (const [sym, icon] of Object.entries(ICONS)) {
    if (text.startsWith(sym + " ")) {
      return (
        <span style={{ display:"flex", alignItems:"flex-start", gap:5, lineHeight:1.5 }}>
          {icon}
          <span>{text.slice(sym.length + 1)}</span>
        </span>
      );
    }
  }
  return <span>{text}</span>;
}

/* ADD NEW SETPOINT MODAL
   Operators pick a SCADA setpoint from the backend catalog (or type their own).
   The catalog thresholds (range, units, description) are shown as PLACEHOLDERS;
   anything the operator leaves blank falls back to that backend threshold. */
/* The setpoint catalog never changes while the page is open, so it is fetched once
   (as soon as the AI Controller workspace loads) and kept in memory. The modal then
   opens with the dropdown already filled. */
let _setpointCatalogCache = null;
let _setpointCatalogPromise = null;
function loadSetpointCatalog() {
  if (_setpointCatalogCache) return Promise.resolve(_setpointCatalogCache);
  if (!_setpointCatalogPromise) {
    _setpointCatalogPromise = axios.get(`${API}/controller/setpoint-catalog`)
      .then(r => { const list = r.data?.catalog || []; if (list.length) _setpointCatalogCache = list; _setpointCatalogPromise = null; return list; })
      .catch(() => { _setpointCatalogPromise = null; return []; });
  }
  return _setpointCatalogPromise;
}

function AddSetpointModal({ onClose, onAdded, selectedWell }) {
  const [catalog, setCatalog] = useState(_setpointCatalogCache || []);
  const [catLoading, setCatLoading] = useState(!_setpointCatalogCache);
  const [pick, setPick] = useState("");
  const [form, setForm] = useState({ parameter:"", units:"", minimum:"", maximum:"", initial_value:"", description:"", objective:"", risk:"" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    loadSetpointCatalog().then(list => { if (alive) { setCatalog(list); setCatLoading(false); } });
    return () => { alive = false; };
  }, []);

  const picked = useMemo(() => catalog.find(c => c.parameter === pick) || null, [catalog, pick]);
  const grouped = useMemo(() => {
    const g = {};
    catalog.forEach(c => { const k = c.category || "Other"; (g[k] = g[k] || []).push(c); });
    return g;
  }, [catalog]);
  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const onPick = (name) => {
    setPick(name);
    setError("");
    setForm(prev => ({ ...prev, parameter: name || "" }));
  };
  const ph = (key, fallback) => (picked && picked[key] != null && picked[key] !== "") ? String(picked[key]) : fallback;

  const submit = async () => {
    setError("");
    const name = form.parameter.trim();
    if (!name) { setError("Enter a setpoint name."); return; }
    const num = (v, fb) => (v !== "" && v != null) ? Number(v) : (fb ?? null);
    const minimum = num(form.minimum, picked?.minimum);
    const maximum = num(form.maximum, picked?.maximum);
    if (minimum == null || maximum == null || !Number.isFinite(minimum) || !Number.isFinite(maximum)) {
      setError("Minimum and maximum limits are required."); return;
    }
    if (minimum >= maximum) { setError("Minimum must be less than maximum."); return; }
    let initial = null;
    if (form.initial_value !== "") {
      initial = Number(form.initial_value);
      if (!Number.isFinite(initial) || initial < minimum || initial > maximum) {
        setError(`Starting value must be between ${fmt2(minimum)} and ${fmt2(maximum)}.`); return;
      }
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/controller/custom-setpoints`, {
        well: selectedWell || "all",
        operator: "dashboard-operator",
        parameter: name,
        category: picked?.category || "Custom",
        units: form.units.trim() || picked?.units || "",
        minimum, maximum,
        initial_value: initial,
        description: form.description.trim() || picked?.description || "",
        objective: form.objective.trim(),
        risk: form.risk.trim(),
      });
      onAdded(res?.data?.setpoint);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || "Setpoint could not be added.");
    } finally { setSaving(false); }
  };

  const labelStyle = { fontFamily:MONO, fontSize:9, color:C.muted, textTransform:"uppercase", letterSpacing:.7, marginBottom:4 };
  const inputStyle = { width:"100%", boxSizing:"border-box", padding:"7px 9px", background:C.panel, border:`1px solid ${C.border2}`, borderRadius:6, color:C.text, fontFamily:MONO, fontSize:10, outline:"none" };

  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"#000b", zIndex:100000, display:"flex", alignItems:"flex-start", justifyContent:"center", overflowY:"auto", padding:20 }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:C.card, border:`1px solid ${C.border2}`, borderRadius:14, padding:"20px 22px", width:"100%", maxWidth:620, marginTop:30, boxShadow:"0 12px 40px #000e" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
          <div>
            <div style={{ fontFamily:SANS, fontWeight:700, fontSize:14, color:C.text }}>Add New Setpoint</div>
            <div style={{ fontFamily:MONO, fontSize:9, color:C.muted, marginTop:3 }}>Well: {(selectedWell || "-").split("/").pop()} · pick from the SCADA list or enter your own</div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background:"transparent", border:`1px solid ${C.border2}`, color:C.muted, borderRadius:6, padding:"3px 9px", fontFamily:MONO, fontSize:11, cursor:"pointer" }}>✕</button>
        </div>

        <div style={{ marginBottom:12 }}>
          <div style={labelStyle}>Choose from SCADA setpoint list</div>
          <select value={pick} onChange={e=>onPick(e.target.value)} disabled={saving} style={inputStyle}>
            <option value="">{catLoading ? "Loading list…" : "— Custom setpoint (type your own) —"}</option>
            {Object.keys(grouped).map(cat => (
              <optgroup key={cat} label={cat}>
                {grouped[cat].map(c => <option key={c.parameter} value={c.parameter}>{c.parameter} ({c.units})</option>)}
              </optgroup>
            ))}
          </select>
          {picked && <div style={{ fontFamily:MONO, fontSize:9, color:C.muted, marginTop:5 }}>Greyed-out fields below show the standard thresholds. Leave a field blank to use them, or type your own value.</div>}
        </div>

        <div style={{ marginBottom:12 }}>
          <div style={labelStyle}>Setpoint name *</div>
          <input type="text" value={form.parameter} maxLength={80} onChange={e=>setField("parameter", e.target.value)} disabled={saving} placeholder="e.g. Casing Pressure High Alarm" style={inputStyle} />
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:12 }}>
          <div>
            <div style={labelStyle}>Units</div>
            <input type="text" value={form.units} maxLength={20} onChange={e=>setField("units", e.target.value)} disabled={saving} placeholder={ph("units","psi, min, %…")} style={inputStyle} />
          </div>
          <div>
            <div style={labelStyle}>Min limit *</div>
            <input type="number" step="any" value={form.minimum} onChange={e=>setField("minimum", e.target.value)} disabled={saving} placeholder={ph("minimum","min")} style={inputStyle} />
          </div>
          <div>
            <div style={labelStyle}>Max limit *</div>
            <input type="number" step="any" value={form.maximum} onChange={e=>setField("maximum", e.target.value)} disabled={saving} placeholder={ph("maximum","max")} style={inputStyle} />
          </div>
          <div>
            <div style={labelStyle}>Starting value</div>
            <input type="number" step="any" value={form.initial_value} onChange={e=>setField("initial_value", e.target.value)} disabled={saving} placeholder="optional" style={inputStyle} />
          </div>
        </div>

        <div style={{ marginBottom:12 }}>
          <div style={labelStyle}>Description (what is the range / what does it do)</div>
          <textarea rows={2} value={form.description} maxLength={500} onChange={e=>setField("description", e.target.value)} disabled={saving} placeholder={ph("description","Describe this setpoint and its normal range")} style={{ ...inputStyle, resize:"vertical" }} />
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:12 }}>
          <div>
            <div style={labelStyle}>Operating objective (optional)</div>
            <textarea rows={2} value={form.objective} maxLength={500} onChange={e=>setField("objective", e.target.value)} disabled={saving} style={{ ...inputStyle, resize:"vertical" }} />
          </div>
          <div>
            <div style={labelStyle}>Operational risk (optional)</div>
            <textarea rows={2} value={form.risk} maxLength={500} onChange={e=>setField("risk", e.target.value)} disabled={saving} style={{ ...inputStyle, resize:"vertical" }} />
          </div>
        </div>

        {error && <div style={{ marginBottom:10, padding:"7px 10px", borderRadius:7, background:`${C.red}12`, border:`1px solid ${C.red}55`, fontFamily:MONO, fontSize:10, color:C.red }}>{error}</div>}
        <div style={{ display:"flex", justifyContent:"flex-end", gap:8 }}>
          <button onClick={onClose} disabled={saving} style={{ background:"transparent", border:`1px solid ${C.border}`, color:C.muted, borderRadius:6, padding:"7px 12px", fontFamily:MONO, fontSize:10, cursor:"pointer" }}>Cancel</button>
          <button onClick={submit} disabled={saving} style={{ background:`${C.green}18`, border:`1px solid ${C.green}66`, color:C.green, borderRadius:6, padding:"7px 14px", fontFamily:MONO, fontSize:10, fontWeight:700, cursor:saving?"not-allowed":"pointer" }}>{saving ? "Adding…" : "Add Setpoint"}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* DATA-DRIVEN AI CONTROLLER WORKSPACE
   PINN/RL are intentionally not integrated here yet. Their displayed recommendations
   come from a deterministic, data-driven proxy in the backend and are clearly labelled. */
function ControllerAIWorkspace({ data, selectedWell, interlocked, aiEnabled, onRefresh }) {
  const [busy, setBusy] = useState(null);
  const [openStages, setOpenStages] = useState({});   // stage collapsed when === false
  const [openInfo, setOpenInfo] = useState({});       // per-setpoint detail toggle
  const [manualValues, setManualValues] = useState({});
  const [showAdd, setShowAdd] = useState(false);     // "Add New Setpoint" modal
  useEffect(() => { loadSetpointCatalog(); }, []);    // prefetch so the dropdown opens instantly
  // Instant UI override for the "Current" column right after a successful apply.
  // Cleared automatically once the server returns a changed current_value.
  const [applied, setApplied] = useState({});
  const lastCurrentRef = useRef({});
  const serverRows = data?.setpoints || data?.rows || [];
  // Instant UI for add / remove of custom setpoints: shown immediately, dropped once the server list confirms it
  const [pendingAdds, setPendingAdds] = useState([]);
  const [pendingRemoved, setPendingRemoved] = useState([]);
  const rows = useMemo(() => {
    const base = serverRows.filter(r => !(r.is_custom && pendingRemoved.includes(r.parameter)));
    const have = new Set(base.map(r => r.parameter));
    return [...base, ...pendingAdds.filter(r => !have.has(r.parameter))];
  }, [serverRows, pendingAdds, pendingRemoved]);
  useEffect(() => {
    const names = new Set(serverRows.map(r => r.parameter));
    setPendingAdds(prev => { const next = prev.filter(r => !names.has(r.parameter)); return next.length === prev.length ? prev : next; });
    setPendingRemoved(prev => { const next = prev.filter(n => names.has(n)); return next.length === prev.length ? prev : next; });
  }, [data]);
  useEffect(() => { setPendingAdds([]); setPendingRemoved([]); }, [selectedWell]);
  const safety = data?.prediction_safety || [];

  // New well selected -> drop local overrides
  useEffect(() => {
    setApplied({});
    lastCurrentRef.current = {};
  }, [selectedWell]);

  // Sync input boxes + clear local override whenever the SERVER current_value changes
  // (first load, after apply + refetch, or any backend-side change).
  // If current_value did not change, whatever the operator is typing is left alone.
  useEffect(() => {
    const changedRows = [];
    rows.forEach(row => {
      if (lastCurrentRef.current[row.parameter] !== row.current_value) {
        lastCurrentRef.current[row.parameter] = row.current_value;
        changedRows.push(row);
      }
    });
    if (!changedRows.length) return;
    setManualValues(prev => {
      const next = { ...prev };
      changedRows.forEach(row => {
        if (row.current_value != null) next[row.parameter] = inp2(row.current_value);
      });
      return next;
    });
    setApplied(prev => {
      const next = { ...prev };
      changedRows.forEach(row => { delete next[row.parameter]; });
      return next;
    });
  }, [data]);
  const diagnostics = data?.cycle_diagnostics || {};
  const feedback = data?.automatic_feedback_triggers || [];
  const models = data?.pinn_rl_recommendations || {};

  const applySetpoint = async (row, value, source = "manual") => {
    if (interlocked) return;
    if (source === "ai" && !aiEnabled) {
      window.alert("Enable AI before applying the AI recommendation.");
      return;
    }
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue < row.minimum || numericValue > row.maximum) {
      window.alert(`Value must be between ${fmt2(row.minimum)} and ${fmt2(row.maximum)} ${row.units}.`);
      return;
    }
    setBusy(`${source}:${row.parameter}`);
    try {
      await axios.post(`${API}/controller/apply`, {
        well: selectedWell,
        operator: "dashboard-operator",
        source,
        setpoints: { [row.parameter]: numericValue },
        predicted_flow_mcfd: models?.rl?.recommended_flow_mcfd ?? null,
      });
      setManualValues(prev => ({ ...prev, [row.parameter]: inp2(numericValue) }));
      setApplied(prev => ({ ...prev, [row.parameter]: numericValue }));
      onRefresh();
    } catch (e) {
      window.alert(e?.response?.data?.error || "Setpoint could not be applied.");
    } finally { setBusy(null); }
  };

  // Remove an operator-added (custom) setpoint
  const removeCustomSetpoint = async (row) => {
    if (!window.confirm(`Remove custom setpoint "${row.parameter}" from this well?`)) return;
    try {
      await axios.post(`${API}/controller/custom-setpoints/remove`, { well: selectedWell || "all", parameter: row.parameter });
      setPendingAdds(prev => prev.filter(r => r.parameter !== row.parameter));
      setPendingRemoved(prev => prev.includes(row.parameter) ? prev : [...prev, row.parameter]);
      onRefresh();
    } catch (e) {
      window.alert(e?.response?.data?.error || "Setpoint could not be removed.");
    }
  };

  const handleManualValueChange = (row, value) => {
    setManualValues(prev => ({ ...prev, [row.parameter]: value }));
  };

  const exportRows = (items, filename) => {
    const headers = Object.keys(items[0] || {});
    const csv = [headers.join(","), ...items.map(r => headers.map(h => JSON.stringify(r[h] ?? "")).join(","))].join("\n");
    const blob = new Blob([csv], { type:"text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href=url; a.download=filename; a.click(); URL.revokeObjectURL(url);
  };

  const CardMini = ({ title, value, unit, sub, accent=C.cyan, raw=false }) => (
    <div style={{ background:C.panel, border:`1px solid ${C.border}`, borderRadius:10, padding:"12px 14px" }}>
      <div style={{ fontFamily:MONO, fontSize:9, color:C.muted, textTransform:"uppercase", letterSpacing:.7 }}>{title}</div>
      <div style={{ marginTop:5, fontFamily:SANS, fontWeight:700, fontSize:20, color:accent }}>{value == null ? "-" : (raw ? value : fmt2(value))}{value != null && unit ? <span style={{fontFamily:MONO,fontSize:9,color:C.muted,marginLeft:5}}>{unit}</span> : null}</div>
      {sub && <div style={{ marginTop:4, fontFamily:MONO, fontSize:9, color:C.muted }}>{sub}</div>}
    </div>
  );

  return (
    <>
      <Card accent={C.blue} style={{ marginBottom:18 }}>
        <SectionTitle sub={`Selected well: ${(selectedWell || "-").split("/").pop()}`}>1. Controller-Adjustable Setpoints</SectionTitle>
        <div style={{ marginBottom:12, padding:"8px 10px", borderRadius:8, background:`${C.blue}10`, border:`1px solid ${C.blue}33`, fontFamily:MONO, fontSize:9, color:C.muted }}>
          Apply the recommended setting, or fine-tune manually within safe limits. {interlocked ? "Emergency Stop is active: writes are blocked." : aiEnabled ? "Smart Recommendations are enabled for this well." : "Enable Smart Recommendations to unlock this."}
        </div>
        {/* Three-stage plunger cycle */}
        <div style={{ display:"flex", alignItems:"stretch", gap:8, flexWrap:"wrap", marginBottom:14 }}>
          {[["1. Shut-In","Valve closed · plunger falls · pressure builds",C.red],["2. Valve Open","Valve open · plunger rises · liquid unloads",C.amber],["3. After-Flow","Plunger at top · gas flows freely",C.green]].map(([t,d,col],i)=>(
            <Fragment key={t}>
              {i>0 && <div style={{ alignSelf:"center", color:C.muted, fontFamily:MONO, fontSize:14 }}>→</div>}
              <div style={{ flex:"1 1 180px", background:C.panel, border:`1px solid ${col}55`, borderRadius:9, padding:"8px 11px" }}>
                <div style={{ fontFamily:SANS, fontWeight:700, fontSize:11, color:col }}>{t}</div>
                <div style={{ fontFamily:MONO, fontSize:9, color:C.muted, marginTop:3 }}>{d}</div>
              </div>
            </Fragment>
          ))}
          <div style={{ alignSelf:"center", color:C.muted, fontFamily:MONO, fontSize:9 }}>↻ restarts at Shut-In</div>
        </div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontFamily:MONO, fontSize:10 }}>
            <thead><tr>{["Setpoint","Classification","AI Recommended","Safe Range","Action"].map(h=><th key={h} style={{textAlign:"left",padding:"8px 7px",borderBottom:`1px solid ${C.border}`,color:C.muted}}>{h}</th>)}</tr></thead>
            <tbody>{rows.map((r,idx)=>{
              const manualValue = manualValues[r.parameter] ?? inp2(r.current_value);
              const manualBusy = busy === `manual:${r.parameter}`;
              const aiBusy = busy === `ai:${r.parameter}`;
              const currentShown = applied[r.parameter] ?? r.current_value;
              const stage = r.stage || "Setpoints";
              const stageDisplay = stage.replace(/\bOpen\b/g, "Valve Open");
              const classificationDisplay = (r.classification || "").replace(/\bOpen\b/g, "Valve Open");
              const newStage = idx === 0 || (rows[idx-1].stage || "Setpoints") !== stage;
              const stageOpen = openStages[stage] !== false;
              const infoOpen = !!openInfo[r.parameter];
              const hasInfo = !!(r.what_it_does || r.goal || r.pain_point);
              return (
                <Fragment key={r.parameter}>
                  {newStage && (
                    <tr>
                      <td colSpan={5} style={{padding:"10px 7px 6px",borderBottom:`1px solid ${C.border}`}}>
                        <div onClick={()=>setOpenStages(prev=>({...prev,[stage]:prev[stage]===false}))} style={{cursor:"pointer",display:"flex",alignItems:"center",gap:8}}>
                          <span style={{color:C.cyan,fontSize:10}}>{stageOpen?"▼":"▶"}</span>
                          <span style={{fontFamily:SANS,fontWeight:700,fontSize:11,color:C.text}}>{stageDisplay}</span>
                          {r.stage_goal && <span style={{fontFamily:MONO,fontSize:9,color:C.muted}}>, {r.stage_goal}</span>}
                        </div>
                      </td>
                    </tr>
                  )}
                  {stageOpen && (
                    <tr>
                      <td style={{padding:"9px 7px",color:C.text,fontWeight:700}}>
                        {r.parameter}
                        {hasInfo && <button onClick={()=>setOpenInfo(prev=>({...prev,[r.parameter]:!prev[r.parameter]}))} title="Show / hide details" style={{marginLeft:6,background:"transparent",border:`1px solid ${C.border2}`,color:infoOpen?C.cyan:C.muted,borderRadius:10,width:16,height:16,lineHeight:"13px",fontSize:9,cursor:"pointer",padding:0}}>{infoOpen?"−":"i"}</button>}
                        {r.is_custom && <span style={{marginLeft:6,fontFamily:MONO,fontSize:8,color:C.purple,background:`${C.purple}18`,border:`1px solid ${C.purple}44`,borderRadius:4,padding:"1px 5px"}}>CUSTOM</span>}
                        {r.is_custom && <button onClick={()=>removeCustomSetpoint(r)} title="Remove this custom setpoint" style={{marginLeft:6,background:"transparent",border:`1px solid ${C.border2}`,color:C.muted,borderRadius:10,width:16,height:16,lineHeight:"13px",fontSize:9,cursor:"pointer",padding:0}}>✕</button>}
                        <div style={{color:C.muted,fontSize:9}}>{r.units}</div>
                      </td>
                      <td style={{padding:"9px 7px",color:C.cyan}}>{classificationDisplay}</td>
                      <td style={{padding:"9px 7px",color:applied[r.parameter] != null ? C.amber : C.text}}>{fmt2(currentShown)}</td>
                      <td style={{padding:"9px 7px",color:C.muted}}>{fmt2(r.minimum)} - {fmt2(r.maximum)}</td>
                      <td style={{padding:"9px 7px",minWidth:260}}>
                        <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                          <input
                            type="number"
                            min={r.minimum}
                            max={r.maximum}
                            step="any"
                            value={manualValue}
                            onChange={e=>handleManualValueChange(r,e.target.value)}
                            disabled={interlocked || manualBusy || aiBusy}
                            aria-label={`${r.parameter} manual value`}
                            style={{width:82,padding:"6px 7px",background:C.panel,border:`1px solid ${C.border2}`,borderRadius:6,color:C.text,fontFamily:MONO,fontSize:10,outline:"none"}}
                          />
                          <button
                            disabled={interlocked || manualBusy || aiBusy}
                            onClick={()=>applySetpoint(r, manualValue, "manual")}
                            style={{background:interlocked?C.muted2:`${C.blue}18`,border:`1px solid ${interlocked?C.muted2:C.blue}66`,color:interlocked?C.muted:C.blue,borderRadius:6,padding:"6px 8px",fontFamily:MONO,fontSize:9,cursor:interlocked?"not-allowed":"pointer"}}
                          >{manualBusy?"Applying…":"Apply Manual"}</button>
                          <button
                            disabled={interlocked || !aiEnabled || manualBusy || aiBusy || r.recommended_value == null}
                            onClick={()=>applySetpoint(r, r.recommended_value, "ai")}
                            title={!aiEnabled ? "Enable AI to apply the recommendation" : "Apply the current AI recommendation"}
                            style={{background:(interlocked || !aiEnabled)?C.muted2:`${C.green}18`,border:`1px solid ${(interlocked || !aiEnabled)?C.muted2:`${C.green}66`}`,color:(interlocked || !aiEnabled)?"#ffffff":C.green,WebkitTextFillColor:(interlocked || !aiEnabled)?"#ffffff":C.green,opacity:1,borderRadius:6,padding:"6px 8px",fontFamily:MONO,fontSize:9,fontWeight:700,cursor:(interlocked || !aiEnabled)?"not-allowed":"pointer"}}
                          >{aiBusy?"Applying…":"Apply AI"}</button>
                        </div>
                      </td>
                    </tr>
                  )}
                  {stageOpen && infoOpen && (
                    <tr>
                      <td colSpan={5} style={{padding:"2px 7px 10px"}}>
                        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,fontFamily:MONO,fontSize:9,color:C.muted}}>
                          <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 10px"}}><div style={{color:C.cyan,marginBottom:3}}>OPERATING FUNCTION</div>{r.what_it_does || "-"}</div>
                          <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 10px"}}><div style={{color:C.green,marginBottom:3}}>OPERATING OBJECTIVE</div>{r.goal || "-"}</div>
                          <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 10px"}}><div style={{color:C.red,marginBottom:3}}>OPERATIONAL RISK</div>{r.pain_point || "-"}</div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}</tbody>
          </table>
        </div>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:8, flexWrap:"wrap", marginTop:10 }}>
          <button onClick={()=>setShowAdd(true)} style={{background:`${C.cyan}14`,border:`1px solid ${C.cyan}66`,color:C.cyan,borderRadius:6,padding:"6px 12px",fontFamily:MONO,fontSize:9,fontWeight:700,cursor:"pointer"}}>+ Add New Setpoint</button>
          <button onClick={()=>exportRows(rows,"controller-setpoints.csv")} style={{background:"transparent",border:`1px solid ${C.border}`,color:C.muted,borderRadius:6,padding:"6px 10px",fontFamily:MONO,fontSize:9,cursor:"pointer"}}>Export CSV</button>
        </div>
      </Card>
      {showAdd && <AddSetpointModal selectedWell={selectedWell} onClose={()=>setShowAdd(false)} onAdded={(row)=>{ if (row) setPendingAdds(prev => [...prev, row]); onRefresh(); }} />}

      <Card accent={C.red} style={{ marginBottom:18 }}>
        <SectionTitle>2. AI Recommendation & Rule Engine Validation and Operating Limits</SectionTitle>
        {(() => {
          // Keep the safety review data unchanged; only arrange it visually as
          // PINN (left) and RL (right), aligned by metric.
          const pinnSafety = safety.filter(p => String(p.model || "").toUpperCase().includes("PINN"));
          const rlSafety = safety.filter(p => String(p.model || "").toUpperCase().includes("RL"));
          const safetyRows = Math.max(pinnSafety.length, rlSafety.length);
          const renderSafetyCard = (p, key) => p ? (
            <div key={key} style={{background:C.panel,border:`1px solid ${p.status==="REVIEW REQUIRED"?C.red:C.border}`,borderRadius:9,padding:"11px 13px"}}>
              <div style={{fontFamily:MONO,fontSize:9,color:C.muted}}>{String(p.model || "").toUpperCase().includes("PINN") ? "AI Recommendation" : "Rule Engine"}</div><div style={{fontFamily:SANS,fontWeight:700,fontSize:12,color:C.text,marginTop:3}}>{p.name}</div>
              <div style={{fontFamily:MONO,fontSize:18,color:p.status==="REVIEW REQUIRED"?C.red:C.green,marginTop:5}}>{fmt2(p.prediction)} <span style={{fontSize:9,color:C.muted}}>{p.unit}</span></div>
              <div style={{fontFamily:MONO,fontSize:9,color:p.status==="REVIEW REQUIRED"?C.red:C.muted,marginTop:4}}>{p.status}{p.optimal_min!=null?` · band ${fmt2(p.optimal_min)}-${fmt2(p.optimal_max)}`:""}</div>
              {p.recommendation && <div style={{fontFamily:MONO,fontSize:9,color:C.muted,marginTop:4}}>{p.recommendation}</div>}
            </div>
          ) : <div key={key} />;
          return (
            <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:10}}>
              {Array.from({length:safetyRows}, (_, i) => (
                <Fragment key={`safety-row-${i}`}>
                  {renderSafetyCard(pinnSafety[i], `pinn-${i}`)}
                  {renderSafetyCard(rlSafety[i], `rl-${i}`)}
                </Fragment>
              ))}
            </div>
          );
        })()}
      </Card>

      <Card accent={C.purple} style={{ marginBottom:18 }}>
                <SectionTitle>3. AI Model & Recommendation Layer</SectionTitle>
        <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:12}}>
          <div style={{background:C.panel,border:`1px solid ${C.purple}44`,borderRadius:10,padding:14}}><div style={{fontFamily:SANS,fontWeight:700,color:C.purple}}>AI Recommendation</div><div style={{fontFamily:MONO,fontSize:9,color:C.muted,marginTop:5}}>AI Recommendation</div><div style={{marginTop:10,display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}><CardMini title="Velocity" value={models?.pinn?.predicted_velocity_ftmin} unit="ft/min" accent={C.purple}/><CardMini title="Flow" value={models?.pinn?.predicted_flow_mcfd} unit="MCF/d" accent={C.purple}/><CardMini title="Tubing Target" value={models?.pinn?.tubing_pressure_target_psi} unit="psi" accent={C.purple}/></div></div>
          <div style={{background:C.panel,border:`1px solid ${C.teal}44`,borderRadius:10,padding:14}}><div style={{fontFamily:SANS,fontWeight:700,color:C.teal}}>Rule Engine</div><div style={{fontFamily:MONO,fontSize:9,color:C.muted,marginTop:5}}>Rule Engine</div><div style={{marginTop:10,display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}><CardMini title="Recommended Velocity" value={models?.rl?.recommended_velocity_ftmin} unit="ft/min" accent={C.teal}/><CardMini title="Recommended Flow" value={models?.rl?.recommended_flow_mcfd} unit="MCF/d" accent={C.teal}/><CardMini title="Candidates" value={models?.rl?.candidate_count} accent={C.teal} raw/></div></div>
        </div>
      </Card>

      <Card accent={C.cyan} style={{ marginBottom:18 }}>
        <SectionTitle>4. Cycle Travel & Timing Diagnostics</SectionTitle>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
          <CardMini title="Longest Plunger Travel" value={diagnostics.longest_plunger_travel_min} unit="min"/><CardMini title="Shortest Plunger Travel" value={diagnostics.shortest_plunger_travel_min} unit="min"/><CardMini title="Average Plunger Travel" value={diagnostics.average_plunger_travel_min} unit="min"/><CardMini title="Current Cycle Travel" value={diagnostics.current_cycle_travel_min} unit="min"/><CardMini title="Time Remaining" value={diagnostics.time_remaining_min} unit="min"/><CardMini title="Recovery After Non-Arrival" value={diagnostics.recovery_after_non_arrival_min} unit="min"/><CardMini title="Tubing Over Static Applied" value={diagnostics.tubing_over_static_applied_psi} unit="psi"/><CardMini title="Afterflow Minimum Applied" value={diagnostics.afterflow_minimum_applied_mcfd} unit="MCF/d"/><CardMini title="Target Arrival Time" value={diagnostics.target_arrival_time_min} unit="min"/>
        </div>
      </Card>
    </>
  );
}

/* INTERLOCK */
function InterlockBanner({ interlock, onEngage, onReset }) {
  const engaged = !!interlock?.engaged;
  return (
    <Card accent={engaged ? C.red : C.green} style={{ marginBottom: 18 }}>
      <div style={{ display:"flex", alignItems:"center", gap:14, flexWrap:"wrap" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <PulseDot color={engaged ? C.red : C.green}/>
          <div>
            <div style={{ fontFamily:MONO, fontSize:12, fontWeight:700, color: engaged ? C.red : C.green }}>
              {engaged ? "[BLOCKED] AI CONTROLLER INTERLOCKED" : "[OK] Interlock Clear: AI Recommendations Live"}
            </div>
            {engaged ? (
              <div style={{ fontFamily:MONO, fontSize:10, color:C.muted, marginTop:3, maxWidth:560 }}>
                Reason: {interlock?.reason || "unknown"}
                {interlock?.source === "auto" ? " (auto-detected)" : interlock?.engaged_by ? ` (manually engaged by ${interlock.engaged_by})` : ""}
              </div>
            ) : null}
          </div>
        </div>
        <div style={{ display:"flex", gap:10, marginLeft:"auto" }}>
          {!engaged && (
            <button onClick={onEngage} style={{
              background:`${C.red}18`, border:`1px solid ${C.red}66`, borderRadius:8,
              padding:"9px 16px", fontFamily:SANS, fontWeight:700, fontSize:11,
              color:C.red, cursor:"pointer", letterSpacing:1,
            }}>■ EMERGENCY STOP</button>
          )}
          {engaged && (
            <button onClick={onReset} style={{
              background:`${C.green}18`, border:`1px solid ${C.green}66`, borderRadius:8,
              padding:"9px 16px", fontFamily:SANS, fontWeight:700, fontSize:11,
              color:C.green, cursor:"pointer", letterSpacing:1,
            }}>↺ RESET INTERLOCK</button>
          )}
        </div>
      </div>
    </Card>
  );
}

function ControllerAISummary({ selectedWell, filteredData, setpointData, predictions, interlockData, aiEnabled }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState("");
  const [error, setError] = useState("");
  const requestKey = useRef("");

  const makePayload = useCallback(() => {
    const numeric = (key) => filteredData
      .map(row => Number(row?.[key]))
      .filter(value => Number.isFinite(value));
    const metrics = {};
    ["ArrivalVelocity", "FlowRateAtClose", "CsgAtOpen", "TbgAtOpen", "ArrivalMinute", "VentMin", "TimeOffTotal", "TimeAfterFlow"].forEach(key => {
      const values = numeric(key);
      if (values.length) {
        metrics[key] = {
          count: values.length,
          latest: values[values.length - 1],
          min: Math.min(...values),
          max: Math.max(...values),
          mean: values.reduce((a, b) => a + b, 0) / values.length,
          recent_values: values.slice(-12),
        };
      }
    });
    return {
      title: "AI Controller operating summary",
      subtitle: "Current selected well, filtered cycle data, controller state and model output",
      scope: `Well: ${selectedWell || "all"}; cycles in current view: ${filteredData.length}`,
      lens: {
        role: "assess current plunger-lift cycle performance, controller readiness, model evidence and safe next checks",
        factors: [
          "recent measured cycle metrics and their range",
          "predicted versus measured behaviour where provided",
          "AI enablement and interlock state",
          "available controller setpoint recommendations and their safety status"
        ]
      },
      charts: [{
        name: "Measured cycle metrics",
        metrics,
        controller: {
          ai_enabled: Boolean(aiEnabled),
          interlock_engaged: Boolean(interlockData?.engaged),
          interlock_reason: interlockData?.reason || null,
          setpoints: setpointData?.setpoints || setpointData?.recommendations || setpointData || null,
          model_output: predictions?.predictions || predictions?.data || predictions || null
        }
      }]
    };
  }, [selectedWell, filteredData, setpointData, predictions, interlockData, aiEnabled]);

  const loadSummary = useCallback(async () => {
    const payload = makePayload();
    const key = JSON.stringify(payload);
    if (requestKey.current === key && summary) return;
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API}/plot-summary`, payload);
      const text = String(response.data?.summary || "").trim();
      if (!text) throw new Error(response.data?.error || "The AI summary service returned no summary.");
      requestKey.current = key;
      setSummary(text);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "AI summary could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [makePayload, summary]);

  return (
    <div style={{ marginBottom: 18, background:C.panel, border:`1px solid ${C.border}`, borderRadius:10, padding:"12px 16px" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
        <div>
          <div style={{ fontFamily:SANS, fontWeight:700, fontSize:12, color:C.text }}>AI Controller Summary</div>
          <div style={{ fontFamily:MONO, fontSize:9, color:C.muted, marginTop:4 }}>Generated from the selected well’s current data and controller state</div>
        </div>
        <button onClick={() => {
          const next = !open;
          setOpen(next);
          if (next) loadSummary();
        }} style={{
          background:open ? `${C.cyan}18` : C.card, border:`1px solid ${C.cyan}66`,
          borderRadius:7, padding:"8px 12px", color:C.cyan, cursor:"pointer",
          fontFamily:MONO, fontSize:10, fontWeight:700
        }}>{open ? "HIDE AI SUMMARY −" : "SHOW AI SUMMARY +"}</button>
      </div>
      {open && (
        <div style={{ marginTop:12, borderTop:`1px solid ${C.border}`, paddingTop:12 }}>
          {loading ? <div style={{ color:C.muted, fontFamily:MONO, fontSize:10 }}>Generating summary from current data…</div>
            : error ? <div style={{ color:C.amber, fontFamily:MONO, fontSize:10 }}>{error} <button onClick={loadSummary} style={{ marginLeft:8, background:"transparent", border:`1px solid ${C.border2}`, color:C.cyan, padding:"4px 8px", borderRadius:5, cursor:"pointer" }}>Retry</button></div>
            : <div style={{ color:C.text, fontFamily:"'Barlow', sans-serif", fontSize:13, lineHeight:1.65, whiteSpace:"pre-wrap" }}>{summary}</div>}
        </div>
      )}
    </div>
  );
}

export default function AIControllerPage() {
  const { isDark } = useTheme();
  applyTheme(getColors(isDark));
  const [filters,     setFilters]     = useState({ well:"Alpha-Well", limit:"200" });
  const [mode,        setMode]        = useState("cp");
  const [predictions, setPredictions] = useState(null);
  const [predLoading, setPredLoading] = useState(false);
  const [showParams,  setShowParams]  = useState(false);
  const [refreshing,  setRefreshing]  = useState(false);
  const [refreshKey,  setRefreshKey]  = useState(0);   
  const { data:rawCyclesData } = useFetch(`${API}/cycles/timeseries?limit=200`, refreshKey);
  const { data:shutinSum }     = useFetch(`${API}/shutin/summary`,              refreshKey);
  const { data:afterflowSum }  = useFetch(`${API}/afterflow/summary`,           refreshKey);
  const { data:modelInfo }     = useFetch(`${API}/model-info`);   
  const { data:wellSpecs }     = useFetch(`${API}/well-specs`);
  const { selectedWell } = useWell();
  const { data:predSeries }    = useFetch(`${API}/controller/predicted-series?well=${encodeURIComponent(selectedWell || "all")}&limit=200`, refreshKey);
  // FIX: setpoints now re-fetch whenever refreshKey changes (apply / refresh)
  const [setpointKey, setSetpointKey] = useState(0);
  const [mountTs] = useState(() => Date.now());
  const { data:setpointData }  = useFetch(`${API}/controller/setpoints?well=${encodeURIComponent(selectedWell || "all")}&_t=${mountTs}-${refreshKey + setpointKey}`, refreshKey + setpointKey);
  // AI is ENABLED BY DEFAULT: the button shows "✓ AI ENABLED" from the first render, and the
  // effect below makes sure the backend is switched on for the selected well as well.
  const [aiEnabled, setAiEnabled] = useState(true);
  useEffect(() => {
    let cancelled = false;
    const well = selectedWell || "all";
    setAiEnabled(true);
    axios.get(`${API}/controller/ai?well=${encodeURIComponent(well)}`)
      .then(r => {
        if (cancelled) return;
        if (r.data?.enabled) { setAiEnabled(true); return; }
        // backend says OFF -> switch it ON so the default is real, not just visual
        return axios.post(`${API}/controller/ai`, { well, enabled:true })
          .then(p => { if (!cancelled) setAiEnabled(p.data?.enabled !== false); })
          .catch(() => { if (!cancelled) setAiEnabled(true); });
      })
      .catch(() => { if (!cancelled) setAiEnabled(true); });
    return () => { cancelled = true; };
  }, [selectedWell]);
  const toggleAI = useCallback(async () => {
    try {
      const next = !aiEnabled;
      const r = await axios.post(`${API}/controller/ai`, { well:selectedWell || "all", enabled:next });
      setAiEnabled(!!r.data?.enabled);
    } catch (e) { window.alert(e?.response?.data?.error || "AI state could not be changed."); }
  }, [aiEnabled, selectedWell]);
  const [interlockKey, setInterlockKey] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setInterlockKey(k => k + 1), 5000);
    return () => clearInterval(t);
  }, []);
  const { data:interlockData } = useFetch(`${API}/controller/interlock`, interlockKey);

  const [overrideInterlock, setOverrideInterlock] = useState(false);
  useEffect(() => {
    // Force a fresh, conscious decision each time a NEW interlock event fires.
    if (interlockData?.engaged) setOverrideInterlock(false);
  }, [interlockData?.engaged, interlockData?.engaged_at]);

  const engageInterlock = useCallback(async (reason) => {
    try { await axios.post(`${API}/controller/interlock/engage`, { reason: reason || null, operator: "dashboard-operator" }); }
    catch (e) { console.error("Failed to engage interlock:", e); }
    setInterlockKey(k => k + 1);
  }, []);

  const handleEngage = useCallback(() => {
    const reason = window.prompt("Enter the reason for engaging the interlock (why is this well being stopped?):");
    if (!reason || !reason.trim()) return;
    const operator = window.prompt("Enter your name/ID:");
    if (!operator || !operator.trim()) return;
    engageInterlock(reason.trim(), operator.trim());
  }, [engageInterlock]);

  const resetInterlock = useCallback(async () => {
    const operator = window.prompt("Confirm interlock reset: enter your name/ID:");
    if (!operator) return;
    try { await axios.post(`${API}/controller/interlock/reset`, { confirm: true, operator }); }
    catch (e) { console.error("Failed to reset interlock:", e); }
    setInterlockKey(k => k + 1);
  }, []);

  const [win, setWin] = useState({ mode: "all", from: "", to: "" });
  // rows after the existing "Max Cycles" filter, each stamped with its time in ms (_t)
  const baseData = useMemo(() => {
    const allRows = rawCyclesData?.data || [];
    if (!allRows.length) return [];
    return allRows.slice(0, parseInt(filters.limit) || 200).map(r => ({ ...r, _t: parseT(r.time) }));
  }, [rawCyclesData, filters.limit]);
  // rows after the time window: this is what every plot on the page receives
  const filteredData = useMemo(() => applyWindow(baseData, win), [baseData, win]);
  const runPredictions = useCallback(async (limit) => {
    setPredLoading(true);
    try {
      const r = await axios.post(`${API}/predict/batch`, { n: parseInt(limit || filters.limit) });
      setPredictions(r.data);
    } catch(e) { console.error("Deep Sequence Neural Network predict failed:", e); }
    setPredLoading(false);
  }, [filters.limit]);

  useEffect(() => { runPredictions(); }, []);   
  const handleRefresh = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      setRefreshKey(k => k + 1);           
      await runPredictions(filters.limit); 
    } catch(e) {
      console.error("Refresh failed:", e);
    }
    setRefreshing(false);
  }, [refreshing, runPredictions, filters.limit]);

  // FIX: light callback used after a setpoint apply: only re-fetches the setpoints table
  // (no heavy /predict/batch re-run and not blocked by the `refreshing` guard).
  const handleSetpointApplied = useCallback(() => {
    setSetpointKey(k => k + 1);
  }, []);

  const targetValues = {
    pr: afterflowSum?.FlowRateAtClose?.mean,
    cp: shutinSum?.summary?.CsgAtOpen?.mean,
    tp: shutinSum?.summary?.TbgAtOpen?.mean,
  };

  return (
    <div>
      <FilterBar filters={filters} onChange={setFilters}/>

      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:18, padding:"10px 16px", background:C.panel, border:`1px solid ${C.border}`, borderRadius:10, fontFamily:MONO, fontSize:10 }}>
        <span style={{ color:C.muted }}>Showing:</span>
        <span style={{ color:C.cyan, fontWeight:700 }}>{filteredData.length} cycles</span>
        <span style={{ color:C.muted }}>│ Active mode:</span>
        <span style={{ color: mode==="pr" ? C.teal : mode==="cp" ? C.cyan : C.amber, fontWeight:700 }}>
          {mode==="pr" ? "Production Rate" : mode==="cp" ? "Casing Pressure" : "Tubing Over"}
        </span>
      </div>

      <ControllerAISummary selectedWell={selectedWell} filteredData={filteredData} setpointData={setpointData} predictions={predictions} interlockData={interlockData} aiEnabled={aiEnabled}/>

      <ControllerHeader mode={mode} onModeChange={setMode} onParams={() => setShowParams(true)} targetValues={targetValues} interlocked={interlockData?.engaged} aiEnabled={aiEnabled} onToggleAI={toggleAI}/>

      <InterlockBanner interlock={interlockData} onEngage={() => engageInterlock(null)} onReset={resetInterlock}/>

      <ControllerAIWorkspace data={setpointData} selectedWell={selectedWell} interlocked={interlockData?.engaged} aiEnabled={aiEnabled} onRefresh={handleSetpointApplied}/>

      <TimeWindowBar win={win} setWin={setWin} base={baseData} view={filteredData}/>

      <ProductionAndVelocity filteredData={filteredData} shutinData={shutinSum} afterflowData={afterflowSum} predictions={predictions} predSeries={predSeries} mode={mode} onModeChange={setMode}/>
      <VelocitySafeBand filteredData={filteredData}/>
      <NeuralNetworkModel filteredData={filteredData} mode={mode} onModeChange={setMode}/>
      <LearningWindowsAnalysis filteredData={filteredData}/>
      <DiagnosticsPanel filteredData={filteredData}/>

      {showParams && (
        <ParametersModal
          onClose      ={() => setShowParams(false)}
          modelInfo    ={modelInfo}
          shutinData   ={shutinSum}
          afterflowData={afterflowSum}
          wellSpecs    ={wellSpecs}
          setpoints    ={setpointData}
        />
      )}
    </div>
  );
}